import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { FixedSizeList as List } from "react-window";
import "./PianoFerie.css";
import FiltroCategorie from "../components/FiltroCategorie/FiltroCategorie";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import Lf23Loader from "../components/Lf23Loader";

// Giorni festivi italiani per anno
const getGiorniFestivi = (anno) => {
    const pasquaMap = {
        2023: { month: 3, day: 9 },
        2024: { month: 2, day: 31 },
        2025: { month: 3, day: 20 },
        2026: { month: 3, day: 5 },
    };
    const pasqua = pasquaMap[anno] || { month: 3, day: 20 };
    const pasquetta = new Date(anno, pasqua.month, pasqua.day + 1);

    return [
        { date: `${anno}-01-01`, name: "Capodanno" },
        { date: `${anno}-01-06`, name: "Epifania" },
        { date: `${anno}-${String(pasqua.month + 1).padStart(2, '0')}-${String(pasqua.day).padStart(2, '0')}`, name: "Pasqua" },
        { date: `${anno}-${String(pasquetta.getMonth() + 1).padStart(2, '0')}-${String(pasquetta.getDate()).padStart(2, '0')}`, name: "Pasquetta" },
        { date: `${anno}-04-25`, name: "Festa della Liberazione" },
        { date: `${anno}-05-01`, name: "Festa del Lavoro" },
        { date: `${anno}-06-02`, name: "Festa della Repubblica" },
        { date: `${anno}-08-15`, name: "Ferragosto" },
        { date: `${anno}-11-01`, name: "Ognissanti" },
        { date: `${anno}-12-08`, name: "Immacolata Concezione" },
        { date: `${anno}-12-25`, name: "Natale" },
        { date: `${anno}-12-26`, name: "Santo Stefano" },
    ];
};

// Cache festivi per anno
const festiviCache = {};
const getFestiviForYear = (anno) => {
    if (!festiviCache[anno]) {
        const festivi = getGiorniFestivi(anno);
        festiviCache[anno] = {
            set: new Set(festivi.map(f => f.date)),
            map: new Map(festivi.map(f => [f.date, f.name]))
        };
    }
    return festiviCache[anno];
};

// Dati mock dipendenti - RIMOSSO, ora caricati da Interacta
// const mockDipendenti = [...];

// Stati ferie con colori
const STATI_FERIE = {
    confermata: { label: 'Confermata', color: '#10B981', desc: 'Ferie approvate' },
    nuova_richiesta: { label: 'Nuova richiesta', color: '#F59E0B', desc: 'In attesa di approvazione' },
    rifiutata: { label: 'Rifiutata', color: '#F87171', desc: 'Non approvate' },
    rifiutata_bozza: { label: 'Rifiutata (da trasmettere)', color: '#F87171', desc: 'Rifiutata ma non ancora trasmessa a Interacta' },
    rifiutata_accordo: { label: 'Rifiutata in accordo', color: '#DC2626', desc: 'Rifiutata con accordo col dipendente' },
    proposta_rifiutata: { label: 'Proposta Rifiutata', color: '#F87171', desc: 'Settimana originale rifiutata per spostamento' },
    spostata_bozza: { label: 'Spostata (da trasmettere)', color: '#38BDF8', desc: 'Spostata ma non ancora trasmessa a Interacta' },
    spostata: { label: 'Spostata', color: '#38BDF8', desc: 'Nuova settimana proposta per spostamento' },
    spostata_accordo: { label: 'Spostata in Accordo', color: '#3B82F6', desc: 'Spostata con accordo col dipendente' },
    bozza: { label: 'Bozza', color: '#9CA3AF', desc: 'Bozza richiesta (non inviata)' }
};

// Settimane annuali da fissare
const SETTIMANE_ANNUALI = 4;
const MAX_POST_INTERACTA = 4;
const MAX_SETTIMANE_ESTIVE = 2;
const GIORNI_FERIE_ANNUALI = 28;

// Helper per generare il tooltip degli errori (testo semplice per title nativo)
const getAlertTooltip = (stats) => {
    const errori = [];
    if (stats.errori?.superaLimiteSettimane) {
        errori.push(`${stats.settimaneContate} settimane richieste (max ${SETTIMANE_ANNUALI})`);
    }
    if (stats.errori?.superaLimitePost) {
        errori.push(`${stats.postIdContati} richieste Interacta (max ${MAX_POST_INTERACTA})`);
    }
    if (stats.errori?.superaLimiteEstivo) {
        errori.push(`${stats.settimaneEstive} settimane nel periodo estivo (max ${MAX_SETTIMANE_ESTIVE})`);
    }
    return errori.length > 0 ? `Attenzione:\n${errori.join('\n')}` : '';
};

// Helper per ottenere lista errori come array
const getAlertErrors = (stats) => {
    const errori = [];
    if (stats.errori?.superaLimiteSettimane) {
        errori.push(`${stats.settimaneContate} settimane richieste (max ${SETTIMANE_ANNUALI})`);
    }
    if (stats.errori?.superaLimitePost) {
        errori.push(`${stats.postIdContati} richieste Interacta (max ${MAX_POST_INTERACTA})`);
    }
    if (stats.errori?.superaLimiteEstivo) {
        errori.push(`${stats.settimaneEstive} settimane nel periodo estivo (max ${MAX_SETTIMANE_ESTIVE})`);
    }
    return errori;
};

// Ottieni il numero di settimana ISO per una data
const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Ottieni il lunedì di una settimana ISO
const getMondayOfWeek = (anno, weekNum) => {
    // Calcola il primo giovedì dell'anno (ISO week 1 contiene il primo giovedì)
    const jan4 = new Date(anno, 0, 4);
    const dayOfWeek = jan4.getDay() || 7; // Domenica = 7

    // Trova il lunedì della settimana 1
    const week1Monday = new Date(jan4);
    week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);

    // Calcola il lunedì della settimana richiesta
    const targetMonday = new Date(week1Monday);
    targetMonday.setDate(week1Monday.getDate() + (weekNum - 1) * 7);

    return targetMonday;
};

// Genera ferie mock per settimane - RIMOSSO, ora caricati da Interacta
// const generateMockFerieWeekly = (dipendenteId, anno) => {...};

// Genera struttura settimane per un anno
// Genera struttura settimane per anno fiscale (Aprile-Marzo)
// annoFiscale = anno di inizio (es. 2026 per periodo Apr 2026 - Mar 2027)
// Settimane: S14 dell'anno iniziale fino a S13 dell'anno successivo
const generateWeeksStructure = (annoFiscale) => {
    const weeks = [];
    const annoInizio = annoFiscale;
    const annoFine = annoFiscale + 1;

    // Verifica se l'anno ha 53 settimane ISO
    // Un anno ha 53 settimane se inizia di giovedì, o se è bisestile e inizia di mercoledì
    const hasWeek53 = (anno) => {
        const jan1 = new Date(anno, 0, 1);
        const dayOfWeek = jan1.getDay(); // 0=Dom, 1=Lun, ..., 4=Gio
        const isLeapYear = (anno % 4 === 0 && anno % 100 !== 0) || (anno % 400 === 0);
        return dayOfWeek === 4 || (isLeapYear && dayOfWeek === 3);
    };

    const lastWeekOfYear = hasWeek53(annoInizio) ? 53 : 52;

    // Prima parte: S14 - S52/S53 dell'anno di inizio (Aprile - Dicembre/Gennaio)
    for (let w = 14; w <= lastWeekOfYear; w++) {
        const mondayBase = getMondayOfWeek(annoInizio, w);
        const monday = new Date(mondayBase.getTime());
        const sunday = new Date(monday.getTime());
        sunday.setDate(monday.getDate() + 6);

        // Usa il giovedì per determinare il mese (standard ISO)
        const thursday = new Date(monday.getTime());
        thursday.setDate(monday.getDate() + 3);
        const month = thursday.getMonth();

        // Calcola fiscalMonthIndex: Apr(3)=0, May(4)=1, ..., Dec(11)=8
        // Per S53 che sconfina in gennaio, consideriamolo ancora come dicembre (fiscalMonthIndex=8)
        let fiscalMonthIndex;
        if (month >= 3) {
            fiscalMonthIndex = month - 3; // Apr(3)=0, ..., Dec(11)=8
        } else {
            // Gennaio (0) per S53 a cavallo d'anno -> trattalo come fine dicembre = 8
            fiscalMonthIndex = 8;
        }
        fiscalMonthIndex = Math.max(0, Math.min(8, fiscalMonthIndex));

        weeks.push({
            weekNum: w,
            anno: annoInizio,
            startDate: new Date(monday.getTime()),
            endDate: new Date(sunday.getTime()),
            month: month,
            fiscalMonthIndex: fiscalMonthIndex
        });
    }

    // Seconda parte: S1 - S13 dell'anno successivo (Gennaio - Marzo)
    for (let w = 1; w <= 13; w++) {
        const mondayBase = getMondayOfWeek(annoFine, w);
        const monday = new Date(mondayBase.getTime());
        const sunday = new Date(monday.getTime());
        sunday.setDate(monday.getDate() + 6);

        const thursday = new Date(monday.getTime());
        thursday.setDate(monday.getDate() + 3);
        const month = thursday.getMonth();

        // Per S1-S13: Jan(0)=9, Feb(1)=10, Mar(2)=11
        // Edge cases:
        // - S1 potrebbe avere giovedì in dicembre -> fiscalMonthIndex = 9 (Gennaio)
        // - S13 potrebbe avere giovedì in aprile -> fiscalMonthIndex = 11 (Marzo)
        let fiscalMonthIndex;
        if (month <= 2) {
            // Gen, Feb, Mar -> 9, 10, 11
            fiscalMonthIndex = month + 9;
        } else if (month === 11) {
            // Dicembre (edge case per S1 a cavallo d'anno) -> consideriamo come Gen = 9
            fiscalMonthIndex = 9;
        } else if (month === 3) {
            // Aprile (edge case per S13 a cavallo d'anno) -> consideriamo come Mar = 11
            fiscalMonthIndex = 11;
        } else {
            // Altro (improbabile) -> default a 9
            fiscalMonthIndex = 9;
        }

        weeks.push({
            weekNum: w,
            anno: annoFine,
            startDate: new Date(monday.getTime()),
            endDate: new Date(sunday.getTime()),
            month: month,
            fiscalMonthIndex: fiscalMonthIndex
        });
    }

    return weeks;
};

// Genera struttura mesi per anno fiscale (Aprile-Marzo)
// annoFiscale = anno di inizio (es. 2026 per periodo Apr 2026 - Mar 2027)
const generateMonthsStructure = (annoFiscale) => {
    const months = [];
    const annoInizio = annoFiscale;
    const annoFine = annoFiscale + 1;

    // Calcola giorni per mese considerando anno bisestile
    const getGiorniMese = (anno) => {
        const giorni = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        if ((anno % 4 === 0 && anno % 100 !== 0) || (anno % 400 === 0)) {
            giorni[1] = 29;
        }
        return giorni;
    };

    const giorniInizio = getGiorniMese(annoInizio);
    const giorniFine = getGiorniMese(annoFine);

    // Mesi Apr-Dec dell'anno di inizio (indici 3-11)
    for (let m = 3; m <= 11; m++) {
        const days = [];
        for (let d = 1; d <= 31; d++) {
            if (d <= giorniInizio[m]) {
                const date = new Date(annoInizio, m, d);
                const dateStr = `${annoInizio}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                days.push({
                    date: dateStr,
                    dayOfWeek: date.getDay(),
                    dayOfMonth: d,
                    month: m,
                    anno: annoInizio,
                    weekNum: getWeekNumber(date),
                    valid: true,
                    fiscalMonthIndex: m - 3 // Apr=0, May=1, ..., Dec=8
                });
            } else {
                days.push({ valid: false, month: m, fiscalMonthIndex: m - 3 });
            }
        }
        months.push(days);
    }

    // Mesi Jan-Mar dell'anno successivo (indici 0-2)
    for (let m = 0; m <= 2; m++) {
        const days = [];
        for (let d = 1; d <= 31; d++) {
            if (d <= giorniFine[m]) {
                const date = new Date(annoFine, m, d);
                const dateStr = `${annoFine}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                days.push({
                    date: dateStr,
                    dayOfWeek: date.getDay(),
                    dayOfMonth: d,
                    month: m,
                    anno: annoFine,
                    weekNum: getWeekNumber(date),
                    valid: true,
                    fiscalMonthIndex: m + 9 // Jan=9, Feb=10, Mar=11
                });
            } else {
                days.push({ valid: false, month: m, fiscalMonthIndex: m + 9 });
            }
        }
        months.push(days);
    }

    return months;
};

const mesiNomi = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
// Nomi mesi in ordine fiscale (Apr-Mar)
const mesiFiscali = ["Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic", "Gen", "Feb", "Mar"];
const giorniAbbr = ["D", "L", "M", "M", "G", "V", "S"];

// Tipo drag per react-dnd
const DRAG_TYPE = "FERIE_WEEK";

// Formatta data per tooltip (dd/mm)
const formatDateShort = (date) => {
    if (!date) return '--/--';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '--/--';
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Componente cella settimana draggabile
const DraggableWeekCell = ({ week, stato, dipendenteId, onDrop, onClick, onEmptyClick, canDrag, originalWeekFrom, onCancelMove }) => {
    const [{ isDragging }, drag] = useDrag({
        type: DRAG_TYPE,
        item: { dipendenteId, weekNum: week.weekNum, stato, originalWeekFrom },
        canDrag: () => canDrag && (stato === 'nuova_richiesta' || stato === 'spostata_bozza'),
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    });

    const [{ isOver, canDropHere }, drop] = useDrop({
        accept: DRAG_TYPE,
        canDrop: (item) => {
            // Stesso dipendente e settimana diversa
            if (item.dipendenteId !== dipendenteId || item.weekNum === week.weekNum) return false;

            // Caso normale: cella vuota
            if (!stato) return true;

            // Caso speciale: spostata_bozza trascinata sulla sua rifiutata_bozza originale = annulla
            if (stato === 'rifiutata_bozza' &&
                item.stato === 'spostata_bozza' &&
                item.originalWeekFrom === week.weekNum) {
                return true;
            }

            return false;
        },
        drop: (item) => {
            // Caso speciale: annulla spostamento
            if (stato === 'rifiutata_bozza' &&
                item.stato === 'spostata_bozza' &&
                item.originalWeekFrom === week.weekNum) {
                onCancelMove(item.dipendenteId, week.weekNum, item.weekNum);
                return;
            }
            // Caso normale: sposta
            onDrop(item.weekNum, week.weekNum, item.stato, item.originalWeekFrom);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDropHere: monitor.canDrop()
        })
    });

    const ref = (node) => {
        drag(drop(node));
    };

    const handleClick = () => {
        if (stato && onClick) {
            onClick(week.weekNum, stato);
        } else if (!stato && onEmptyClick) {
            onEmptyClick(week.weekNum);
        }
    };

    const cellClass = `week-cell ${stato ? `ferie-${stato} clickable` : 'empty-clickable'} ${isDragging ? 'dragging' : ''} ${isOver && canDropHere ? 'drop-target' : ''} ${(stato === 'nuova_richiesta' || stato === 'spostata_bozza') ? 'draggable' : ''}`;

    // Tooltip con date inizio/fine
    const startDate = formatDateShort(week.startDate);
    const endDate = formatDateShort(week.endDate);
    const tooltipText = stato
        ? `${STATI_FERIE[stato]?.label || stato} - S${week.weekNum}: ${startDate} - ${endDate}`
        : `S${week.weekNum}: ${startDate} - ${endDate} (clicca per creare bozza)`;

    return (
        <div
            ref={ref}
            className={cellClass}
            onClick={handleClick}
            title={tooltipText}
        />
    );
};

// Componente riga colonna fissa (virtualizzata)
const FixedColumnRow = React.memo(({ index, style, data }) => {
    const {
        filteredDipendenti,
        getStats,
        selectedYear,
        SETTIMANE_ANNUALI,
        GIORNI_FERIE_ANNUALI,
        handleHideRow
    } = data;

    const dipendente = filteredDipendenti[index];
    if (!dipendente) return null;

    const stats = getStats(dipendente.id, selectedYear);

    return (
        <div style={style} className="fixed-row-wrapper">
            <div className={`fixed-main-row ${stats.superaLimite ? 'alert-row' : ''}`}>
                <div className="row-spacer" style={{ width: '28px', marginLeft: '8px' }}></div>
                <button
                    className="hide-btn"
                    onClick={() => handleHideRow(dipendente.id)}
                    title="Nascondi riga"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                </button>
                <div className="calendar-info">
                    <div className="dipendente-nome">
                        {dipendente.cognome} {dipendente.nome}
                        {stats.superaLimite && (
                            <span className="alert-badge" title={getAlertTooltip(stats)}>
                                !
                            </span>
                        )}
                    </div>
                </div>
                <div className="calendar-assegnazione">
                    <span className="badge-assegnazione">{dipendente.assegnazione_dett || '-'}</span>
                </div>
                <div className="calendar-richieste">
                    <span className={`counter-value ${stats.giorniRichiesti >= GIORNI_FERIE_ANNUALI ? 'green' : 'red'}`}>
                        {stats.giorniRichiesti}
                    </span>
                </div>
                <div className="calendar-conferme">
                    <span className={`counter-value ${stats.giorniConfermati >= GIORNI_FERIE_ANNUALI ? 'green' : 'red'}`}>
                        {stats.giorniConfermati}
                    </span>
                </div>
            </div>
        </div>
    );
});

// Componente riga calendario (virtualizzata)
const ScrollColumnRow = React.memo(({ index, style, data }) => {
    const {
        filteredDipendenti,
        viewMode,
        renderWeekView,
        renderDayView
    } = data;

    const dipendente = filteredDipendenti[index];
    if (!dipendente) return null;

    return (
        <div style={style} className="scroll-row-wrapper">
            <div className="scroll-main-row">
                {viewMode === 'settimane' ? renderWeekView(dipendente) : renderDayView(dipendente)}
            </div>
        </div>
    );
});

// Componente SearchInput isolato - gestisce il proprio stato per evitare re-render del parent
const SearchInput = React.memo(({ onSearch, onClear, initialValue = '' }) => {
    const [value, setValue] = useState(initialValue);
    const [hasAppliedSearch, setHasAppliedSearch] = useState(!!initialValue);

    const handleSearch = () => {
        onSearch(value);
        setHasAppliedSearch(!!value);
    };

    const handleClear = () => {
        setValue('');
        onClear();
        setHasAppliedSearch(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div className="search-bar-compact" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
                type="text"
                placeholder="Cerca dipendente..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{ flex: 1 }}
            />
            {(value || hasAppliedSearch) && (
                <button
                    onClick={handleClear}
                    title="Cancella ricerca"
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#94a3b8',
                        flexShrink: 0
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            )}
            <button
                onClick={handleSearch}
                style={{
                    background: '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    flexShrink: 0,
                    transition: 'all 0.2s'
                }}
            >
                Cerca
            </button>
        </div>
    );
});

// Componente principale
const PianoFerie = () => {
    const navigate = useNavigate();
    const currentYear = new Date().getFullYear();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    // Stati
    const [viewMode, setViewMode] = useState('settimane'); // 'settimane' o 'giorni'
    const [filtroCanitere, setFiltroCantiere] = useState([]);
    const [filtroCommessa, setFiltroCommessa] = useState([]);
    const [filtroAssegnazione, setFiltroAssegnazione] = useState([]);
    const [filtroStato, setFiltroStato] = useState('tutti'); // 'tutti', 'errore', 'con_richieste'
    const [filtroStatoNew, setFiltroStatoNew] = useState('tutti'); // Filtro stato per tab Pianificazione
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(""); // Valore applicato al filtro (dopo click su Cerca)
    const [isFiltering, setIsFiltering] = useState(false); // Loading durante il filtraggio
    const [showLegenda, setShowLegenda] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedTab, setSelectedTab] = useState('Pianificazione');
    const [expandedAssegnazioni, setExpandedAssegnazioni] = useState(new Set());
    // Configurazione operatori richiesti caricata dal DB
    const [operatoriRichiestiConfig, setOperatoriRichiestiConfig] = useState({}); // { assegnazioneKey: { default: X, settimane: { weekNum-anno: Y } } }
    const [editingCell, setEditingCell] = useState(null); // { assegnazioneKey, weekNum, anno, type: 'settimana' | 'default' }
    // Selezione multipla celle (drag selection)
    const [dragSelection, setDragSelection] = useState(null); // { assegnazioneKey, startWeek, startAnno, endWeek, endAnno, isSelecting }
    const [showBulkEditPopup, setShowBulkEditPopup] = useState(null); // { assegnazioneKey, selectedWeeks: [{weekNum, anno}], position: {x, y} }
    const [contextMenu, setContextMenu] = useState(null); // { x, y, assegnazioneKey, weekNum, anno }
    const [filtroCantiereAssegnazioni, setFiltroCantiereAssegnazioni] = useState([]);
    const [filtroCommessaAssegnazioni, setFiltroCommessaAssegnazioni] = useState([]);
    const [filtroAssegnazioneAssegnazioni, setFiltroAssegnazioneAssegnazioni] = useState([]);
    const [filtroCriticita, setFiltroCriticita] = useState(false);
    const [showFiltersAssegnazioni, setShowFiltersAssegnazioni] = useState(false);
    const [isFullscreenAssegnazioni, setIsFullscreenAssegnazioni] = useState(false);
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);
    const [showInfoAssegnazioni, setShowInfoAssegnazioni] = useState(false);
    const [showInfoAssegnazioneNew, setShowInfoAssegnazioneNew] = useState(false);
    const [filtroStatoAssegnazioneNew, setFiltroStatoAssegnazioneNew] = useState('tutti');
    const [alertTooltip, setAlertTooltip] = useState(null); // { x, y, errors: [] }
    const [showInfo, setShowInfo] = useState(false);
    const [hiddenRows, setHiddenRows] = useState(new Set());
    const [showHiddenSection, setShowHiddenSection] = useState(false);
    const [ferieData, setFerieData] = useState({});
    const [showMoveDialog, setShowMoveDialog] = useState(null);
    const [showRecapModal, setShowRecapModal] = useState(null); // { dipendenteId, weekNum, stato, startDate, endDate, dipendenteName }
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [bozzeData, setBozzeData] = useState({}); // { `${dipendenteId}-${weekNum}-${anno}`: true }
    // spostamentiBozza: { `${dipendenteId}-${weekTo}-${anno}`: { weekFrom, weekTo } }
    // La chiave è la settimana DESTINAZIONE per mostrare il blocchetto lì
    const [spostamentiBozza, setSpostamentiBozza] = useState({});
    const [showRiepilogoSpostamenti, setShowRiepilogoSpostamenti] = useState(false);
    const [trasmettiInCorso, setTrasmettiInCorso] = useState({}); // { chiave: true } per spinner singolo
    const [filtroCantiereRiepilogo, setFiltroCantiereRiepilogo] = useState([]);
    const [filtroCommessaRiepilogo, setFiltroCommessaRiepilogo] = useState([]);
    const [filtroAssegnazioneRiepilogo, setFiltroAssegnazioneRiepilogo] = useState([]);
    const [searchRiepilogo, setSearchRiepilogo] = useState('');
    const [showDipendentePendingModal, setShowDipendentePendingModal] = useState(null); // { dipendenteId, dipendenteName }
    const [trasmettiPendingInCorso, setTrasmettiPendingInCorso] = useState({}); // { chiave: true } per spinner singolo
    const [trasmettiBozzaInCorso, setTrasmettiBozzaInCorso] = useState(false); // loading per trasmissione bozza

    // Stati per dati Interacta
    const [dipendenti, setDipendenti] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [errorData, setErrorData] = useState(null);

    // Totali e stats pre-calcolati dal backend
    const [totali, setTotali] = useState({ giorniRichiesti: 0, giorniConfermati: 0, perStato: {} });
    const [statsPerDipendente, setStatsPerDipendente] = useState({});

    // Funzione per applicare la ricerca (chiamata dal componente SearchInput)
    const handleSearch = useCallback((term) => {
        setIsFiltering(true);
        setTimeout(() => {
            setAppliedSearchTerm(term);
            setIsFiltering(false);
        }, 50);
    }, []);

    // Funzione per cancellare la ricerca
    const handleClearSearch = useCallback(() => {
        setIsFiltering(true);
        setTimeout(() => {
            setAppliedSearchTerm('');
            setIsFiltering(false);
        }, 50);
    }, []);

    // Refs per scroll sync (Pianificazione)
    const scrollHeaderRef = useRef(null);
    const scrollCounterRef = useRef(null);
    const scrollBodyRef = useRef(null);
    const fixedBodyRef = useRef(null);


    // Refs per liste virtualizzate
    const fixedListRef = useRef(null);
    const scrollListRef = useRef(null);

    // Limite righe per performance - infinite scroll
    const ROWS_PER_PAGE = 50;
    const [visibleRowsCount, setVisibleRowsCount] = useState(ROWS_PER_PAGE);

    // Escape key per uscire dal fullscreen Assegnazioni
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                if (isFullscreenAssegnazioni) setIsFullscreenAssegnazioni(false);
                if (isFullscreenTable) setIsFullscreenTable(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreenAssegnazioni, isFullscreenTable]);

    // Carica dati da Interacta (dipendenti + ferie)
    // Carica dati per anno fiscale (es. 2026 -> Aprile 2026 - Marzo 2027)
    // Richiede dati per entrambi gli anni
    useEffect(() => {
        const fetchPianoFerieData = async () => {
            setLoadingData(true);
            setErrorData(null);
            try {
                const annoInizio = selectedYear;
                const annoFine = selectedYear + 1;

                // Fetch dati per entrambi gli anni dell'anno fiscale
                const response = await fetch(`${API_URL}/piano-ferie/interacta/piano-ferie?annoInizio=${annoInizio}&annoFine=${annoFine}`);
                if (!response.ok) {
                    throw new Error(`Errore HTTP: ${response.status}`);
                }
                const data = await response.json();

                // Imposta i dipendenti
                setDipendenti(data.dipendenti || []);

                // Imposta i dati ferie (contiene entrambi gli anni)
                setFerieData(data.ferieData || {});

                // Imposta totali e stats pre-calcolati dal backend
                if (data.totali) {
                    setTotali(data.totali);
                }
                if (data.statsPerDipendente) {
                    setStatsPerDipendente(data.statsPerDipendente);
                }

                console.log(`Caricati ${data.totalDipendenti} dipendenti e ${data.totalPostsFerie} post ferie per anno fiscale ${annoInizio}-${annoFine}`);
            } catch (error) {
                console.error('Errore nel caricamento dati da Interacta:', error);
                setErrorData(error.message);
            } finally {
                setLoadingData(false);
            }
        };

        fetchPianoFerieData();
    }, [selectedYear, API_URL]);

    // Toggle espansione riga
    const toggleExpandRow = (dipendenteId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(dipendenteId)) {
                newSet.delete(dipendenteId);
            } else {
                newSet.add(dipendenteId);
            }
            return newSet;
        });
    };

    // Carica configurazione unificata dal backend (hidden-rows, bozze, spostamenti-bozza, operatori-config)
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${API_URL}/piano-ferie/config?anno=${selectedYear}`);
                if (response.ok) {
                    const data = await response.json();

                    // Hidden rows
                    setHiddenRows(new Set(data.hiddenIds));

                    // Bozze
                    const bozzeMap = {};
                    data.bozze.forEach(b => {
                        bozzeMap[`${b.dipendenteId}-${b.weekNum}-${b.anno}`] = true;
                    });
                    setBozzeData(bozzeMap);

                    // Spostamenti bozza
                    const spostamentiMap = {};
                    const weeks = generateWeeksStructure(selectedYear);
                    const weekAnnoMap = {};
                    weeks.forEach(w => { weekAnnoMap[w.weekNum] = w.anno; });
                    data.spostamenti.forEach(s => {
                        // Chiave = settimana DESTINAZIONE con anno reale della settimana
                        const destAnno = weekAnnoMap[s.weekTo] || s.anno;
                        spostamentiMap[`${s.dipendenteId}-${s.weekTo}-${destAnno}`] = {
                            weekFrom: s.weekFrom,
                            weekTo: s.weekTo,
                            postId: s.postId
                        };
                    });
                    setSpostamentiBozza(spostamentiMap);

                    // Operatori richiesti config
                    setOperatoriRichiestiConfig(data.operatoriRichiestiConfig || {});
                }
            } catch (error) {
                console.error('Errore nel caricamento configurazione piano ferie:', error);
            }
        };
        fetchConfig();
    }, [selectedYear, API_URL]);

    // Salva configurazione operatori richiesti al backend
    const saveOperatoriRichiestiConfig = async (assegnazioneKey, weekNum, anno, valore, tipo) => {
        try {
            const response = await fetch(`${API_URL}/piano-ferie/operatori-richiesti-config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ assegnazioneKey, weekNum, anno, valore, tipo })
            });
            if (!response.ok) {
                console.error('Errore nel salvataggio configurazione');
            }
        } catch (error) {
            console.error('Errore nel salvataggio configurazione:', error);
        }
    };

    // Crea bozza
    const handleCreateBozza = async (dipendenteId, weekNum, anno) => {
        const annoEffettivo = anno || selectedYear;
        try {
            const response = await fetch(`${API_URL}/piano-ferie/bozze`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    dipendenteId,
                    weekNum,
                    anno: annoEffettivo
                })
            });

            if (response.ok) {
                setBozzeData(prev => ({
                    ...prev,
                    [`${dipendenteId}-${weekNum}-${annoEffettivo}`]: true
                }));
            }
        } catch (error) {
            console.error('Errore nella creazione bozza:', error);
        }
    };

    // Elimina bozza
    const handleDeleteBozza = async (dipendenteId, weekNum, anno) => {
        const annoEffettivo = anno || selectedYear;
        try {
            const response = await fetch(
                `${API_URL}/piano-ferie/bozze/${dipendenteId}/${weekNum}/${annoEffettivo}`,
                { method: 'DELETE' }
            );

            if (response.ok) {
                setBozzeData(prev => {
                    const newData = { ...prev };
                    delete newData[`${dipendenteId}-${weekNum}-${annoEffettivo}`];
                    return newData;
                });
                setShowRecapModal(null);
            }
        } catch (error) {
            console.error('Errore nell\'eliminazione bozza:', error);
        }
    };

    // Trasmetti bozza a Interacta (crea nuovo post)
    const handleTrasmettiBozza = async (dipendenteId, weekNum, anno) => {
        const annoEffettivo = anno || selectedYear;
        setTrasmettiBozzaInCorso(true);
        try {
            const response = await fetch(`${API_URL}/piano-ferie/bozze/trasmetti`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    dipendenteId,
                    weekNum,
                    anno: annoEffettivo,
                    dipendenteInteractaId: dipendenteId // l'id del dipendente è già l'id Interacta
                })
            });

            if (response.ok) {
                const result = await response.json();

                // Rimuovi la bozza dallo stato locale
                setBozzeData(prev => {
                    const newData = { ...prev };
                    delete newData[`${dipendenteId}-${weekNum}-${annoEffettivo}`];
                    return newData;
                });

                // Aggiungi la nuova richiesta ai ferieData locali (evita refresh completo)
                const postId = result.postId || null;
                const weekData = weeksStructure.find(w => w.weekNum === weekNum && w.anno === annoEffettivo);
                if (weekData) {
                    setFerieData(prev => {
                        const newData = { ...prev };
                        if (!newData[dipendenteId]) newData[dipendenteId] = {};
                        if (!newData[dipendenteId][annoEffettivo]) newData[dipendenteId][annoEffettivo] = [];
                        // Aggiungi 7 giorni per la settimana (come fa processFeriePosts)
                        const startDate = new Date(weekData.startDate);
                        for (let d = 0; d < 7; d++) {
                            const date = new Date(startDate);
                            date.setDate(startDate.getDate() + d);
                            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                            newData[dipendenteId][annoEffettivo] = [
                                ...newData[dipendenteId][annoEffettivo],
                                { date: dateStr, stato: 'spostata', weekNum, postId }
                            ];
                        }
                        return newData;
                    });

                    // Aggiorna stats del dipendente
                    setStatsPerDipendente(prev => {
                        const newStats = { ...prev };
                        const base = newStats[dipendenteId] || { giorniRichiesti: 0, giorniConfermati: 0, settimaneContate: 0, postIdContati: 0, settimaneEstive: 0, superaLimite: false, errori: {} };
                        newStats[dipendenteId] = {
                            ...base,
                            giorniRichiesti: base.giorniRichiesti + 7,
                            settimaneContate: base.settimaneContate + 1,
                            postIdContati: base.postIdContati + (postId ? 1 : 0)
                        };
                        return newStats;
                    });
                }

                setShowRecapModal(null);
            } else {
                const data = await response.json();
                alert(`Errore trasmissione: ${data.error || 'Errore sconosciuto'}`);
            }
        } catch (error) {
            console.error('Errore nella trasmissione bozza:', error);
            alert(`Errore nella trasmissione: ${error.message}`);
        } finally {
            setTrasmettiBozzaInCorso(false);
        }
    };

    // Sync scroll handlers
    const handleBodyScroll = useCallback((e) => {
        if (scrollHeaderRef.current) {
            scrollHeaderRef.current.scrollLeft = e.target.scrollLeft;
        }
        if (scrollCounterRef.current) {
            scrollCounterRef.current.scrollLeft = e.target.scrollLeft;
        }
    }, []);

    const handleHeaderScroll = useCallback((e) => {
        if (scrollBodyRef.current) {
            scrollBodyRef.current.scrollLeft = e.target.scrollLeft;
        }
        if (scrollCounterRef.current) {
            scrollCounterRef.current.scrollLeft = e.target.scrollLeft;
        }
    }, []);

    const handleScrollBodyVertical = useCallback((e) => {
        if (fixedBodyRef.current) {
            fixedBodyRef.current.scrollTop = e.target.scrollTop;
        }
    }, []);

    const handleFixedBodyVertical = useCallback((e) => {
        if (scrollBodyRef.current) {
            scrollBodyRef.current.scrollTop = e.target.scrollTop;
        }
    }, []);


    const handleHeaderWheel = useCallback((e) => {
        if (e.deltaX !== 0) {
            e.preventDefault();
            if (scrollBodyRef.current) {
                scrollBodyRef.current.scrollLeft += e.deltaX;
            }
            if (scrollCounterRef.current) {
                scrollCounterRef.current.scrollLeft += e.deltaX;
            }
        }
    }, []);

    useEffect(() => {
        const scrollHeader = scrollHeaderRef.current;
        const scrollBody = scrollBodyRef.current;
        const fixedBody = fixedBodyRef.current;

        if (scrollHeader) {
            scrollHeader.addEventListener('scroll', handleHeaderScroll);
            scrollHeader.addEventListener('wheel', handleHeaderWheel, { passive: false });
        }
        if (scrollBody) {
            scrollBody.addEventListener('scroll', handleBodyScroll);
            scrollBody.addEventListener('scroll', handleScrollBodyVertical);
        }
        if (fixedBody) {
            fixedBody.addEventListener('scroll', handleFixedBodyVertical);
        }

        return () => {
            if (scrollHeader) {
                scrollHeader.removeEventListener('scroll', handleHeaderScroll);
                scrollHeader.removeEventListener('wheel', handleHeaderWheel);
            }
            if (scrollBody) {
                scrollBody.removeEventListener('scroll', handleBodyScroll);
                scrollBody.removeEventListener('scroll', handleScrollBodyVertical);
            }
            if (fixedBody) {
                fixedBody.removeEventListener('scroll', handleFixedBodyVertical);
            }
        };
    }, [handleBodyScroll, handleHeaderScroll, handleHeaderWheel, handleScrollBodyVertical, handleFixedBodyVertical, loadingData]);


    // Filtri
    const cantieri = [...new Set(dipendenti.map(d => d.cantiere).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const commesse = [...new Set(dipendenti.map(d => d.commessa).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const assegnazioni = [...new Set(dipendenti.map(d => d.assegnazione_dett).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const assegnazioniGroup = [...new Set(dipendenti.map(d => d.assegnazione).filter(Boolean))].sort((a, b) => a.localeCompare(b));

    // Anno fiscale: Aprile anno N - Marzo anno N+1
    const yearOptions = [
        { value: currentYear, label: `${currentYear}-${currentYear + 1}` },
        { value: currentYear - 1, label: `${currentYear - 1}-${currentYear}` },
    ];

    const cantiereOptions = cantieri.map(c => ({ value: c, label: c }));
    const commessaOptions = commesse.map(c => ({ value: c, label: c }));
    const assegnazioneOptions = assegnazioni.map(a => ({ value: a, label: a }));
    const assegnazioneGroupOptions = assegnazioniGroup.map(a => ({ value: a, label: a }));

    // Dipendenti filtrati (esclusi nascosti) - usa appliedSearchTerm (dopo click su Cerca)
    const filteredDipendenti = useMemo(() => {
        const searchLower = appliedSearchTerm.toLowerCase();
        return dipendenti.filter(dip => {
            if (hiddenRows.has(dip.id)) return false;

            // Multiselect: se array vuoto = tutti, altrimenti deve matchare uno dei valori
            const matchCantiere = filtroCanitere.length === 0 ||
                filtroCanitere.some(f => f.value === dip.cantiere);
            const matchCommessa = filtroCommessa.length === 0 ||
                filtroCommessa.some(f => f.value === dip.commessa);
            const matchAssegnazione = filtroAssegnazione.length === 0 ||
                filtroAssegnazione.some(f => f.value === dip.assegnazione_dett);

            const matchSearch = !appliedSearchTerm ||
                `${dip.cognome} ${dip.nome}`.toLowerCase().includes(searchLower) ||
                (dip.cantiere && dip.cantiere.toLowerCase().includes(searchLower)) ||
                (dip.commessa && dip.commessa.toLowerCase().includes(searchLower));

            // Filtro stato richieste
            const stats = statsPerDipendente[dip.id] || { giorniRichiesti: 0, superaLimite: false };
            let matchStato = true;
            if (filtroStato === 'errore') {
                matchStato = stats.superaLimite === true;
            } else if (filtroStato === 'con_richieste') {
                matchStato = stats.giorniRichiesti > 0;
            }

            return matchCantiere && matchCommessa && matchAssegnazione && matchSearch && matchStato;
        }).sort((a, b) => a.cognome.localeCompare(b.cognome));
    }, [dipendenti, hiddenRows, filtroCanitere, filtroCommessa, filtroAssegnazione, appliedSearchTerm, filtroStato, statsPerDipendente]);

    // Dipendenti visibili (limitati per performance - infinite scroll)
    const visibleDipendenti = useMemo(() => {
        return filteredDipendenti.slice(0, visibleRowsCount);
    }, [filteredDipendenti, visibleRowsCount]);

    // Conta bozze per dipendente (settimane non già coperte da Interacta)
    const bozzePerDipendente = useMemo(() => {
        const result = {};
        const statiRichiesta = ['nuova_richiesta', 'confermata', 'spostata', 'spostata_accordo'];

        // Mappa settimane valide nell'anno fiscale corrente per lookup veloce
        const weeks = generateWeeksStructure(selectedYear);
        const settimaneValide = new Set(weeks.map(w => `${w.weekNum}-${w.anno}`));

        Object.keys(bozzeData).forEach(key => {
            const parts = key.split('-');
            const dipId = parseInt(parts[0]);
            const weekNum = parseInt(parts[1]);
            const anno = parseInt(parts[2]);

            // Ignora bozze orfane che non corrispondono a settimane dell'anno fiscale
            if (!settimaneValide.has(`${weekNum}-${anno}`)) return;

            // Verifica che la settimana non sia già coperta da uno stato Interacta
            const ferie = ferieData[dipId]?.[anno] || [];
            const haInteracta = ferie.some(f => f.weekNum === weekNum && statiRichiesta.includes(f.stato));
            if (haInteracta) return;

            if (!result[dipId]) result[dipId] = 0;
            result[dipId]++;
        });

        // Conta anche dipendenti con spostamenti bozza
        Object.keys(spostamentiBozza).forEach(key => {
            const parts = key.split('-');
            const dipId = parseInt(parts[0]);
            if (!result[dipId]) result[dipId] = 0;
            result[dipId]++;
        });

        return result;
    }, [bozzeData, ferieData, selectedYear, spostamentiBozza]);

    // Dipendenti filtrati per tab Pianificazione (filtro stato ampliato)
    const filteredDipendentiNew = useMemo(() => {
        const searchLower = appliedSearchTerm.toLowerCase();
        return dipendenti.filter(dip => {
            if (hiddenRows.has(dip.id)) return false;

            const matchCantiere = filtroCanitere.length === 0 ||
                filtroCanitere.some(f => f.value === dip.cantiere);
            const matchCommessa = filtroCommessa.length === 0 ||
                filtroCommessa.some(f => f.value === dip.commessa);
            const matchAssegnazione = filtroAssegnazione.length === 0 ||
                filtroAssegnazione.some(f => f.value === dip.assegnazione_dett);

            const matchSearch = !appliedSearchTerm ||
                `${dip.cognome} ${dip.nome}`.toLowerCase().includes(searchLower) ||
                (dip.cantiere && dip.cantiere.toLowerCase().includes(searchLower)) ||
                (dip.commessa && dip.commessa.toLowerCase().includes(searchLower));

            // Filtro stato richieste ampliato
            const stats = statsPerDipendente[dip.id] || { giorniRichiesti: 0, giorniConfermati: 0, superaLimite: false };
            let matchStato = true;
            if (filtroStatoNew === 'errore') {
                matchStato = stats.superaLimite === true;
            } else if (filtroStatoNew === 'nessuna_richiesta') {
                matchStato = stats.giorniRichiesti === 0;
            } else if (filtroStatoNew === 'richieste_parziali') {
                matchStato = stats.giorniRichiesti > 0 && stats.giorniRichiesti < GIORNI_FERIE_ANNUALI;
            } else if (filtroStatoNew === 'richieste_complete') {
                matchStato = stats.giorniRichiesti >= GIORNI_FERIE_ANNUALI;
            } else if (filtroStatoNew === 'nessuna_conferma') {
                matchStato = stats.giorniConfermati === 0;
            } else if (filtroStatoNew === 'conferme_parziali') {
                matchStato = stats.giorniConfermati > 0 && stats.giorniConfermati < GIORNI_FERIE_ANNUALI;
            } else if (filtroStatoNew === 'con_bozze') {
                matchStato = (bozzePerDipendente[dip.id] || 0) > 0;
            }

            return matchCantiere && matchCommessa && matchAssegnazione && matchSearch && matchStato;
        }).sort((a, b) => a.cognome.localeCompare(b.cognome));
    }, [dipendenti, hiddenRows, filtroCanitere, filtroCommessa, filtroAssegnazione, appliedSearchTerm, filtroStatoNew, statsPerDipendente, bozzePerDipendente]);

    // Dipendenti visibili per tab Pianificazione (infinite scroll)
    const visibleDipendentiNew = useMemo(() => {
        return filteredDipendentiNew.slice(0, visibleRowsCount);
    }, [filteredDipendentiNew, visibleRowsCount]);

    // Raggruppamento gerarchico: Commessa → Assegnazioni → Dipendenti (per tab Assegnazioni)
    const commesseGrouped = useMemo(() => {
        const cantieriMap = {};

        // Raggruppa per Cantiere → Assegnazione (escludi nascosti)
        dipendenti.filter(dip => !hiddenRows.has(dip.id)).forEach(dip => {
            const cantiere = dip.cantiere || 'Non assegnato';
            const assegnazione = dip.assegnazione || 'Non assegnato';

            if (!cantieriMap[cantiere]) {
                cantieriMap[cantiere] = {
                    cantiere,
                    assegnazioni: {},
                    totaleDipendenti: 0
                };
            }

            if (!cantieriMap[cantiere].assegnazioni[assegnazione]) {
                cantieriMap[cantiere].assegnazioni[assegnazione] = {
                    assegnazione,
                    dipendenti: []
                };
            }

            cantieriMap[cantiere].assegnazioni[assegnazione].dipendenti.push(dip);
            cantieriMap[cantiere].totaleDipendenti++;
        });

        // Converti in array e ordina
        return Object.values(cantieriMap)
            .map(c => ({
                ...c,
                assegnazioni: Object.values(c.assegnazioni).sort((a, b) =>
                    a.assegnazione.localeCompare(b.assegnazione)
                )
            }))
            .sort((a, b) => a.cantiere.localeCompare(b.cantiere));
    }, [dipendenti, hiddenRows]);

    // Stati separati per espansione commesse e assegnazioni
    const [expandedCommesse, setExpandedCommesse] = useState(new Set());

    // Toggle espansione commessa
    const toggleExpandCommessa = (commessa) => {
        setExpandedCommesse(prev => {
            const newSet = new Set(prev);
            if (newSet.has(commessa)) {
                newSet.delete(commessa);
            } else {
                newSet.add(commessa);
            }
            return newSet;
        });
    };

    // Toggle espansione assegnazione (per vedere dipendenti)
    const toggleExpandAssegnazione = (key) => {
        setExpandedAssegnazioni(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    // Calcola operatori disponibili per settimana (quelli senza ferie né bozze)
    const getOperatoriDisponibili = useCallback((dipendentiList, weekNum, anno) => {
        return dipendentiList.filter(dip => {
            // Verifica se questa settimana è origine di uno spostamento bozza (la ferie è stata spostata via)
            // La chiave contiene l'anno della destinazione, ma cerchiamo per weekFrom indipendentemente dall'anno della chiave
            const isOrigineSposta = Object.entries(spostamentiBozza).some(([key, value]) =>
                key.startsWith(`${dip.id}-`) && value.weekFrom === weekNum
            );
            if (isOrigineSposta) return true;

            const ferie = ferieData[dip.id]?.[anno] || [];
            const hasFerieInteracta = ferie.some(f => f.weekNum === weekNum && ['confermata', 'nuova_richiesta', 'spostata', 'spostata_accordo'].includes(f.stato));
            if (hasFerieInteracta) return false;
            const hasBozza = bozzeData[`${dip.id}-${weekNum}-${anno}`];
            const hasSpostDest = spostamentiBozza[`${dip.id}-${weekNum}-${anno}`];
            if (hasBozza || hasSpostDest) return false;
            return true;
        }).length;
    }, [ferieData, bozzeData, spostamentiBozza]);

    // Default operatori richiesti: 0 (l'utente imposta manualmente per ogni assegnazione)
    const getOperatoriRichiesti = useCallback(() => {
        return 0;
    }, []);

    // Ottieni operatori richiesti default per assegnazione (editabile o calcolato)
    const getOperatoriRichiestiDefault = useCallback((assegnazioneKey, totaleOperatori) => {
        const config = operatoriRichiestiConfig[assegnazioneKey];
        if (config?.default !== null && config?.default !== undefined) {
            return config.default;
        }
        // Default: 0
        return 0;
    }, [operatoriRichiestiConfig]);

    // Verifica se il default è stato editato
    const isDefaultEdited = useCallback((assegnazioneKey) => {
        const config = operatoriRichiestiConfig[assegnazioneKey];
        return config?.default !== null && config?.default !== undefined;
    }, [operatoriRichiestiConfig]);

    // Ottieni operatori richiesti per una settimana specifica (editabile o default assegnazione)
    const getOperatoriRichiestiSettimana = useCallback((assegnazioneKey, weekNum, anno, totaleOperatori) => {
        const config = operatoriRichiestiConfig[assegnazioneKey];
        const weekKey = `${weekNum}-${anno}`;
        // Prima controlla se c'è un valore specifico per la settimana
        if (config?.settimane?.[weekKey] !== undefined) {
            return config.settimane[weekKey];
        }
        // Altrimenti usa il default per assegnazione (editato o calcolato)
        return getOperatoriRichiestiDefault(assegnazioneKey, totaleOperatori);
    }, [operatoriRichiestiConfig, getOperatoriRichiestiDefault]);

    // Cantieri filtrati per tab Assegnazioni
    const filteredCommesseGrouped = useMemo(() => {
        let result = commesseGrouped;

        // Filtro Cantiere (primo livello)
        if (filtroCantiereAssegnazioni.length > 0) {
            const valori = filtroCantiereAssegnazioni.map(f => f.value);
            result = result.filter(g => valori.includes(g.cantiere));
        }

        // Filtro Assegnazione (secondo livello) e Commessa (sui dipendenti)
        if (filtroAssegnazioneAssegnazioni.length > 0 || filtroCommessaAssegnazioni.length > 0) {
            const valoriAssegnazione = filtroAssegnazioneAssegnazioni.map(f => f.value);
            const valoriCommessa = filtroCommessaAssegnazioni.map(f => f.value);

            result = result.map(cantiereGroup => {
                let assegnazioni = cantiereGroup.assegnazioni;

                if (valoriAssegnazione.length > 0) {
                    assegnazioni = assegnazioni.filter(a => valoriAssegnazione.includes(a.assegnazione));
                }

                if (valoriCommessa.length > 0) {
                    assegnazioni = assegnazioni.map(a => ({
                        ...a,
                        dipendenti: a.dipendenti.filter(dip => valoriCommessa.includes(dip.commessa))
                    })).filter(a => a.dipendenti.length > 0);
                }

                const totaleDipendenti = assegnazioni.reduce((sum, a) => sum + a.dipendenti.length, 0);
                return { ...cantiereGroup, assegnazioni, totaleDipendenti };
            }).filter(g => g.assegnazioni.length > 0);
        }

        // Filtro Criticità: mantieni solo assegnazioni con almeno una settimana in rosso
        if (filtroCriticita) {
            const weeks = generateWeeksStructure(selectedYear);
            result = result.map(cantiereGroup => {
                const assegnazioni = cantiereGroup.assegnazioni.filter(assegnazioneGroup => {
                    const assegnazioneKey = `${cantiereGroup.cantiere}|||${assegnazioneGroup.assegnazione}`;
                    const totale = assegnazioneGroup.dipendenti.length;
                    return weeks.some(week => {
                        const disponibili = getOperatoriDisponibili(assegnazioneGroup.dipendenti, week.weekNum, week.anno);
                        const inFerie = totale - disponibili;
                        const minimoRichiesto = getOperatoriRichiestiSettimana(assegnazioneKey, week.weekNum, week.anno, totale);
                        return inFerie > minimoRichiesto;
                    });
                });
                const totaleDipendenti = assegnazioni.reduce((sum, a) => sum + a.dipendenti.length, 0);
                return { ...cantiereGroup, assegnazioni, totaleDipendenti };
            }).filter(g => g.assegnazioni.length > 0);
        }

        return result;
    }, [commesseGrouped, filtroCommessaAssegnazioni, filtroAssegnazioneAssegnazioni, filtroCantiereAssegnazioni, filtroCriticita, selectedYear, getOperatoriDisponibili, getOperatoriRichiestiSettimana]);

    // Cantieri filtrati per tab Assegnazione (con filtro stato richieste per dipendente)
    const filteredCommesseForAssegnazioneNew = useMemo(() => {
        if (filtroStatoAssegnazioneNew === 'tutti') return filteredCommesseGrouped;

        return filteredCommesseGrouped.map(cantiereGroup => {
            const assegnazioni = cantiereGroup.assegnazioni.map(assegnazioneGroup => {
                const dipendentiFiltrati = assegnazioneGroup.dipendenti.filter(dip => {
                    const stats = statsPerDipendente[dip.id] || { giorniRichiesti: 0, giorniConfermati: 0, superaLimite: false };
                    if (filtroStatoAssegnazioneNew === 'errore') return stats.superaLimite === true;
                    if (filtroStatoAssegnazioneNew === 'nessuna_richiesta') return stats.giorniRichiesti === 0;
                    if (filtroStatoAssegnazioneNew === 'richieste_parziali') return stats.giorniRichiesti > 0 && stats.giorniRichiesti < GIORNI_FERIE_ANNUALI;
                    if (filtroStatoAssegnazioneNew === 'richieste_complete') return stats.giorniRichiesti >= GIORNI_FERIE_ANNUALI;
                    if (filtroStatoAssegnazioneNew === 'nessuna_conferma') return stats.giorniConfermati === 0;
                    if (filtroStatoAssegnazioneNew === 'conferme_parziali') return stats.giorniConfermati > 0 && stats.giorniConfermati < GIORNI_FERIE_ANNUALI;
                    if (filtroStatoAssegnazioneNew === 'con_bozze') return (bozzePerDipendente[dip.id] || 0) > 0;
                    return true;
                });
                return { ...assegnazioneGroup, dipendenti: dipendentiFiltrati };
            }).filter(a => a.dipendenti.length > 0);
            const totaleDipendenti = assegnazioni.reduce((sum, a) => sum + a.dipendenti.length, 0);
            return { ...cantiereGroup, assegnazioni, totaleDipendenti };
        }).filter(g => g.assegnazioni.length > 0);
    }, [filteredCommesseGrouped, filtroStatoAssegnazioneNew, statsPerDipendente, bozzePerDipendente]);

    // Verifica se la settimana è stata editata
    const isSettimanaEdited = useCallback((assegnazioneKey, weekNum, anno) => {
        const config = operatoriRichiestiConfig[assegnazioneKey];
        const weekKey = `${weekNum}-${anno}`;
        return config?.settimane?.[weekKey] !== undefined;
    }, [operatoriRichiestiConfig]);

    // Aggiorna operatori richiesti per una settimana specifica
    const updateOperatoriRichiestiSettimana = useCallback((assegnazioneKey, weekNum, anno, value) => {
        // Aggiorna stato locale
        setOperatoriRichiestiConfig(prev => {
            const newConfig = { ...prev };
            if (!newConfig[assegnazioneKey]) {
                newConfig[assegnazioneKey] = { default: null, settimane: {} };
            }
            newConfig[assegnazioneKey] = {
                ...newConfig[assegnazioneKey],
                settimane: {
                    ...newConfig[assegnazioneKey].settimane,
                    [`${weekNum}-${anno}`]: value
                }
            };
            return newConfig;
        });
        // Salva al backend
        saveOperatoriRichiestiConfig(assegnazioneKey, weekNum, anno, value, 'settimana');
    }, [saveOperatoriRichiestiConfig]);

    // Aggiorna operatori richiesti default per assegnazione
    const updateOperatoriRichiestiDefault = useCallback((assegnazioneKey, value) => {
        // Aggiorna stato locale
        setOperatoriRichiestiConfig(prev => {
            const newConfig = { ...prev };
            if (!newConfig[assegnazioneKey]) {
                newConfig[assegnazioneKey] = { default: null, settimane: {} };
            }
            newConfig[assegnazioneKey] = {
                ...newConfig[assegnazioneKey],
                default: value
            };
            return newConfig;
        });
        // Salva al backend
        saveOperatoriRichiestiConfig(assegnazioneKey, null, null, value, 'default');
    }, [saveOperatoriRichiestiConfig]);

    // Ripristina default per una settimana specifica (rimuove override)
    const resetOperatoriRichiestiSettimana = useCallback(async (assegnazioneKey, weekNum, anno) => {
        // Rimuovi dallo stato locale
        setOperatoriRichiestiConfig(prev => {
            const newConfig = { ...prev };
            if (newConfig[assegnazioneKey]?.settimane) {
                const newSettimane = { ...newConfig[assegnazioneKey].settimane };
                delete newSettimane[`${weekNum}-${anno}`];
                newConfig[assegnazioneKey] = {
                    ...newConfig[assegnazioneKey],
                    settimane: newSettimane
                };
            }
            return newConfig;
        });
        // Elimina dal backend
        try {
            const encodedKey = encodeURIComponent(assegnazioneKey);
            await fetch(`${API_URL}/piano-ferie/operatori-richiesti-config/${encodedKey}/settimana/${weekNum}/${anno}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error('Errore nel ripristino configurazione:', error);
        }
    }, [API_URL]);

    // ===== DRAG SELECTION FUNCTIONS =====

    // Calcola le settimane selezionate dal drag
    const getSelectedWeeksFromDrag = useCallback((selection, weeksList) => {
        if (!selection || !weeksList) return [];

        const { startWeek, startAnno, endWeek, endAnno } = selection;

        // Trova gli indici delle settimane start e end nella lista
        const startIdx = weeksList.findIndex(w => w.weekNum === startWeek && w.anno === startAnno);
        const endIdx = weeksList.findIndex(w => w.weekNum === endWeek && w.anno === endAnno);

        if (startIdx === -1 || endIdx === -1) return [];

        const minIdx = Math.min(startIdx, endIdx);
        const maxIdx = Math.max(startIdx, endIdx);

        return weeksList.slice(minIdx, maxIdx + 1).map(w => ({ weekNum: w.weekNum, anno: w.anno }));
    }, []);

    // Verifica se una cella è nella selezione
    const isCellInSelection = useCallback((assegnazioneKey, weekNum, anno, weeksList) => {
        if (!dragSelection || dragSelection.assegnazioneKey !== assegnazioneKey) return false;

        const selectedWeeks = getSelectedWeeksFromDrag(dragSelection, weeksList);
        return selectedWeeks.some(w => w.weekNum === weekNum && w.anno === anno);
    }, [dragSelection, getSelectedWeeksFromDrag]);

    // Inizia selezione drag (solo tasto sinistro)
    const handleDragStart = useCallback((assegnazioneKey, weekNum, anno, e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        setDragSelection({
            assegnazioneKey,
            startWeek: weekNum,
            startAnno: anno,
            endWeek: weekNum,
            endAnno: anno,
            isSelecting: true
        });
        setEditingCell(null);
        setShowBulkEditPopup(null);
    }, []);

    // Aggiorna selezione durante il drag
    const handleDragMove = useCallback((assegnazioneKey, weekNum, anno) => {
        if (!dragSelection || !dragSelection.isSelecting || dragSelection.assegnazioneKey !== assegnazioneKey) return;

        setDragSelection(prev => ({
            ...prev,
            endWeek: weekNum,
            endAnno: anno
        }));
    }, [dragSelection]);

    // Termina selezione drag
    const handleDragEnd = useCallback((e, weeksList) => {
        if (!dragSelection || !dragSelection.isSelecting) return;

        const selectedWeeks = getSelectedWeeksFromDrag(dragSelection, weeksList);

        if (selectedWeeks.length > 1) {
            // Mostra popup per editing massivo
            setShowBulkEditPopup({
                assegnazioneKey: dragSelection.assegnazioneKey,
                selectedWeeks,
                position: { x: e.clientX, y: e.clientY }
            });
        }

        setDragSelection(prev => prev ? { ...prev, isSelecting: false } : null);
    }, [dragSelection, getSelectedWeeksFromDrag]);

    // Applica valore a tutte le settimane selezionate
    const applyBulkValue = useCallback(async (value) => {
        if (!showBulkEditPopup) return;

        const { assegnazioneKey, selectedWeeks } = showBulkEditPopup;

        // Aggiorna stato locale per tutte le settimane
        setOperatoriRichiestiConfig(prev => {
            const newConfig = { ...prev };
            if (!newConfig[assegnazioneKey]) {
                newConfig[assegnazioneKey] = { default: null, settimane: {} };
            }
            const newSettimane = { ...newConfig[assegnazioneKey].settimane };
            selectedWeeks.forEach(w => {
                newSettimane[`${w.weekNum}-${w.anno}`] = value;
            });
            newConfig[assegnazioneKey] = {
                ...newConfig[assegnazioneKey],
                settimane: newSettimane
            };
            return newConfig;
        });

        // Salva al backend per ogni settimana
        for (const w of selectedWeeks) {
            await saveOperatoriRichiestiConfig(assegnazioneKey, w.weekNum, w.anno, value, 'settimana');
        }

        // Chiudi popup e resetta selezione
        setShowBulkEditPopup(null);
        setDragSelection(null);
    }, [showBulkEditPopup, saveOperatoriRichiestiConfig]);

    // Chiudi selezione (click fuori)
    const cancelSelection = useCallback(() => {
        setDragSelection(null);
        setShowBulkEditPopup(null);
    }, []);

    // Reset righe visibili quando cambiano i filtri
    useEffect(() => {
        setVisibleRowsCount(ROWS_PER_PAGE);
    }, [appliedSearchTerm, filtroCanitere, filtroCommessa, filtroAssegnazione, filtroStato, filtroStatoNew, hiddenRows]);

    // Carica più righe (infinite scroll)
    const maxFiltered = Math.max(filteredDipendenti.length, filteredDipendentiNew.length);
    const loadMoreRows = useCallback(() => {
        if (visibleRowsCount < maxFiltered) {
            setVisibleRowsCount(prev => Math.min(prev + ROWS_PER_PAGE, maxFiltered));
        }
    }, [visibleRowsCount, maxFiltered]);

    // Rileva scroll verso il fondo per caricare più righe
    const handleScrollBottom = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        // Carica più righe quando siamo a 200px dal fondo
        if (scrollHeight - scrollTop - clientHeight < 200) {
            loadMoreRows();
        }
    }, [loadMoreRows]);

    // Dipendenti nascosti
    const hiddenDipendenti = useMemo(() =>
        dipendenti.filter(dip => hiddenRows.has(dip.id)),
        [dipendenti, hiddenRows]
    );

    // Strutture dati
    const weeksStructure = useMemo(() => generateWeeksStructure(selectedYear), [selectedYear]);
    const monthsStructure = useMemo(() => generateMonthsStructure(selectedYear), [selectedYear]);

    // Export Excel per Pianificazione
    const handleExportExcelNew = useCallback(async () => {
        const headers = ['Dipendente', 'Telefono', 'Contratto', 'Assegnazione', 'Richieste', 'Confermate', 'Richieste Ferie'];

        const rows = filteredDipendentiNew.map(dipendente => {
            const stats = statsPerDipendente[dipendente.id] || { giorniRichiesti: 0, giorniConfermati: 0 };
            const ferieAnnoInizio = ferieData[dipendente.id]?.[selectedYear] || [];
            const ferieAnnoFine = ferieData[dipendente.id]?.[selectedYear + 1] || [];
            const tutteFerie = [...ferieAnnoInizio, ...ferieAnnoFine];
            const feriePerSettimana = {};
            tutteFerie.forEach(f => {
                const key = `${f.weekNum}-${f.date?.substring(0,4)}`;
                if (!feriePerSettimana[key]) feriePerSettimana[key] = f;
            });
            const ferieUniche = Object.values(feriePerSettimana)
                .sort((a, b) => {
                    const annoA = parseInt(a.date?.substring(0,4) || '0');
                    const annoB = parseInt(b.date?.substring(0,4) || '0');
                    if (annoA !== annoB) return annoA - annoB;
                    return a.weekNum - b.weekNum;
                });

            const ferieText = ferieUniche.length > 0
                ? ferieUniche.map(f => {
                    const statoLabel = STATI_FERIE[f.stato]?.label || f.stato;
                    const weekInfo = weeksStructure.find(w => w.weekNum === f.weekNum && w.anno === parseInt(f.date?.substring(0,4)));
                    const startStr = weekInfo?.startDate?.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) || '';
                    const endStr = weekInfo?.endDate?.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) || '';
                    return `S${f.weekNum} (${startStr}-${endStr}) ${statoLabel}`;
                }).join(' | ')
                : 'Nessuna richiesta';

            return [
                `${dipendente.cognome} ${dipendente.nome}`,
                [dipendente.cellulare, dipendente.telFisso].filter(Boolean).join(' / ') || '',
                dipendente.contratto || '',
                dipendente.assegnazione_dett || '',
                `${stats.giorniRichiesti}/${GIORNI_FERIE_ANNUALI}`,
                `${stats.giorniConfermati}/${GIORNI_FERIE_ANNUALI}`,
                ferieText
            ];
        });

        try {
            const response = await fetch(`${API_URL}/piano-ferie/downloadExcel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tableName: 'Pianificazione Ferie',
                    datiDaStampare: { headers, rows }
                })
            });

            if (!response.ok) throw new Error('Errore download');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `pianificazione_ferie_${selectedYear}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Errore durante il download del file:', error);
        }
    }, [filteredDipendentiNew, statsPerDipendente, ferieData, selectedYear, weeksStructure, API_URL]);

    // Export Excel per Riepilogo Spostamenti (tab Assegnazione)
    const handleExportExcelRiepilogo = useCallback(async () => {
        const headers = ['Dipendente', 'Telefono', 'Contratto', 'Assegnazione', 'Dal', 'Al'];

        const formatWeekRangeExcel = (weekData) => {
            if (!weekData) return '';
            const start = weekData.startDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
            const end = weekData.endDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
            return `S${weekData.weekNum} ${start} - ${end}`;
        };

        const searchLower = searchRiepilogo.toLowerCase();
        const rows = Object.entries(spostamentiBozza).map(([key, value]) => {
            const parts = key.split('-');
            const dipId = parseInt(parts[0]);
            const weekTo = parseInt(parts[1]);
            const dip = dipendenti.find(d => d.id === dipId);
            const weekFromData = weeksStructure.find(w => w.weekNum === value.weekFrom);
            const weekToData = weeksStructure.find(w => w.weekNum === weekTo);
            return { dip, dipId, weekFromData, weekToData };
        }).filter(({ dip }) => {
            if (!dip) return true;
            const matchCantiere = filtroCantiereRiepilogo.length === 0 || filtroCantiereRiepilogo.some(f => f.value === dip.cantiere);
            const matchCommessa = filtroCommessaRiepilogo.length === 0 || filtroCommessaRiepilogo.some(f => f.value === dip.commessa);
            const matchAssegnazione = filtroAssegnazioneRiepilogo.length === 0 || filtroAssegnazioneRiepilogo.some(f => f.value === dip.assegnazione_dett);
            const matchSearch = !searchRiepilogo || `${dip.cognome} ${dip.nome}`.toLowerCase().includes(searchLower);
            return matchCantiere && matchCommessa && matchAssegnazione && matchSearch;
        }).map(({ dip, dipId, weekFromData, weekToData }) => [
            dip ? `${dip.cognome} ${dip.nome}` : `ID ${dipId}`,
            dip ? [dip.cellulare, dip.telFisso].filter(Boolean).join(' / ') : '',
            dip?.contratto || '',
            dip?.assegnazione_dett || '',
            formatWeekRangeExcel(weekFromData),
            formatWeekRangeExcel(weekToData)
        ]).sort((a, b) => a[0].localeCompare(b[0]));

        try {
            const response = await fetch(`${API_URL}/piano-ferie/downloadExcel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tableName: 'Riepilogo Spostamenti',
                    datiDaStampare: { headers, rows }
                })
            });

            if (!response.ok) throw new Error('Errore download');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `riepilogo_spostamenti.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Errore durante il download del file:', error);
        }
    }, [spostamentiBozza, dipendenti, weeksStructure, selectedYear, API_URL, filtroCantiereRiepilogo, filtroCommessaRiepilogo, filtroAssegnazioneRiepilogo, searchRiepilogo]);

    // Calcola statistiche per dipendente - usa stats pre-calcolate se disponibili
    const getStats = useCallback((dipendenteId, anno) => {
        const bozzeCount = bozzePerDipendente[dipendenteId] || 0;
        const giorniBozze = bozzeCount * 7;

        // Usa stats pre-calcolate dal backend per l'anno selezionato
        if (anno === selectedYear && statsPerDipendente[dipendenteId]) {
            const base = statsPerDipendente[dipendenteId];
            const giorniRichiesti = base.giorniRichiesti + giorniBozze;
            const settimaneContate = base.settimaneContate + bozzeCount;
            const superaLimiteSettimane = settimaneContate > SETTIMANE_ANNUALI;
            return {
                ...base,
                giorniRichiesti,
                settimaneContate,
                superaLimite: superaLimiteSettimane || base.errori.superaLimitePost || base.errori.superaLimiteEstivo,
                errori: {
                    ...base.errori,
                    superaLimiteSettimane
                }
            };
        }

        // Fallback: calcola manualmente per altri anni
        const ferie = ferieData[dipendenteId]?.[anno] || [];
        // Richieste = TUTTE le richieste fatte (pendenti + confermate + spostate)
        // NON include proposta_rifiutata perché rappresenta la settimana "vecchia" sostituita
        const statiRichiesta = ['nuova_richiesta', 'confermata', 'spostata', 'spostata_accordo'];
        // Confermate = solo quelle effettivamente confermate
        const statiConferma = ['confermata', 'spostata_accordo'];

        // Conta settimane uniche (non giorni singoli, per evitare problemi con settimane a cavallo d'anno)
        const ferieRichieste = ferie.filter(f => statiRichiesta.includes(f.stato));
        const settimaneRichieste = new Set(
            ferieRichieste.map(f => f.weekNum)
        );
        const settimaneConfermate = new Set(
            ferie.filter(f => statiConferma.includes(f.stato)).map(f => f.weekNum)
        );

        // Conta POST unici
        const postIdUnici = new Set(
            ferieRichieste.filter(f => f.postId).map(f => f.postId)
        );

        // Calcola settimane estive (08/06 - 06/09) - approssimazione: settimane 24-36
        const settimaneEstiveRange = new Set();
        for (let w = 24; w <= 36; w++) settimaneEstiveRange.add(w);
        const settimaneEstiveRichieste = new Set(
            ferieRichieste.filter(f => settimaneEstiveRange.has(f.weekNum)).map(f => f.weekNum)
        );

        // Moltiplica per 7 per ottenere i giorni equivalenti
        const giorniRichiesti = (settimaneRichieste.size + bozzeCount) * 7;
        const giorniConfermati = settimaneConfermate.size * 7;

        // Errori
        const totalSettimane = settimaneRichieste.size + bozzeCount;
        const superaLimiteSettimane = totalSettimane > SETTIMANE_ANNUALI;
        const superaLimitePost = postIdUnici.size > MAX_POST_INTERACTA;
        const superaLimiteEstivo = settimaneEstiveRichieste.size > MAX_SETTIMANE_ESTIVE;

        return {
            giorniRichiesti,
            giorniConfermati,
            settimaneContate: totalSettimane,
            postIdContati: postIdUnici.size,
            settimaneEstive: settimaneEstiveRichieste.size,
            superaLimite: superaLimiteSettimane || superaLimitePost || superaLimiteEstivo,
            errori: {
                superaLimiteSettimane,
                superaLimitePost,
                superaLimiteEstivo
            }
        };
    }, [ferieData, selectedYear, statsPerDipendente, bozzePerDipendente]);

    // Ottieni lo stato ferie per una settimana specifica
    const getWeekStatus = useCallback((dipendenteId, weekNum, anno) => {
        const ferie = ferieData[dipendenteId]?.[anno] || [];
        const weekFerie = ferie.find(f => f.weekNum === weekNum);
        return weekFerie?.stato || null;
    }, [ferieData]);

    // Ottieni il postId per una settimana specifica
    const getPostIdForWeek = useCallback((dipendenteId, weekNum, anno) => {
        const ferie = ferieData[dipendenteId]?.[anno] || [];
        const weekFerie = ferie.find(f => f.weekNum === weekNum);
        return weekFerie?.postId || null;
    }, [ferieData]);

    // Contatori per header (settimane) - anno fiscale
    const weekCounters = useMemo(() => {
        const counters = {};

        // Prepara mappa spostamenti per lookup veloce (usa anno reale dalla weeksStructure)
        const spostamentiOrigine = {};
        const spostamentiDestinazione = {};
        Object.entries(spostamentiBozza).forEach(([key, value]) => {
            const parts = key.split('-');
            const dipId = parts[0];
            const weekOrigine = weeksStructure.find(w => w.weekNum === value.weekFrom);
            const weekDestinazione = weeksStructure.find(w => w.weekNum === value.weekTo);
            const annoOrigine = weekOrigine ? weekOrigine.anno : selectedYear;
            const annoDestinazione = weekDestinazione ? weekDestinazione.anno : selectedYear;
            spostamentiOrigine[`${dipId}-${value.weekFrom}-${annoOrigine}`] = true;
            spostamentiDestinazione[`${dipId}-${value.weekTo}-${annoDestinazione}`] = true;
        });

        weeksStructure.forEach(week => {
            const weekKey = `${week.weekNum}-${week.anno}`;
            let count = 0;
            filteredDipendenti.forEach(dip => {
                // Usa l'anno della settimana, non selectedYear
                const ferie = ferieData[dip.id]?.[week.anno] || [];
                const weekFerie = ferie.find(f => f.weekNum === week.weekNum);
                const stato = weekFerie?.stato || null;

                const hasBozza = bozzeData[`${dip.id}-${week.weekNum}-${week.anno}`];

                // Verifica se questa settimana è origine/destinazione di uno spostamento bozza
                const isOrigineSposta = spostamentiOrigine[`${dip.id}-${week.weekNum}-${week.anno}`];
                const isDestinazioneSposta = spostamentiDestinazione[`${dip.id}-${week.weekNum}-${week.anno}`];

                // Conta la destinazione dello spostamento bozza
                if (isDestinazioneSposta) {
                    count++;
                }
                // Conta stati normali
                else if (!isOrigineSposta && (stato === 'confermata' || stato === 'nuova_richiesta' || stato === 'spostata' || stato === 'spostata_accordo' || hasBozza)) {
                    count++;
                }
            });
            counters[weekKey] = count;
        });
        return counters;
    }, [weeksStructure, filteredDipendenti, ferieData, bozzeData, spostamentiBozza]);

    // Contatori per header (giorni)
    const dailyCounters = useMemo(() => {
        const counters = {};

        // Prepara mappa spostamenti per lookup veloce
        const spostamentiOrigine = {};
        Object.entries(spostamentiBozza).forEach(([key, value]) => {
            const [dipId] = key.split('-');
            spostamentiOrigine[`${dipId}-${value.weekFrom}`] = true;
        });

        // Pre-computa Set di "${dipId}-${date}" per tutte le date nelle settimane destinazione
        // Questo elimina il .some() + .find() + new Date() dal loop interno (da O(n^3) a O(1) lookup)
        const destinazioneDateSet = new Set();
        Object.entries(spostamentiBozza).forEach(([key, value]) => {
            const [dipId] = key.split('-');
            const weekInfo = weeksStructure.find(w => w.weekNum === value.weekTo);
            if (weekInfo) {
                const start = new Date(weekInfo.startDate);
                const end = new Date(weekInfo.endDate);
                for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                    destinazioneDateSet.add(`${dipId}-${d.toISOString().slice(0, 10)}`);
                }
            }
        });

        monthsStructure.forEach((month) => {
            month.forEach((day) => {
                if (day.valid) {
                    let count = 0;
                    filteredDipendenti.forEach(dip => {
                        const dayYear = parseInt(day.date.substring(0, 4));
                        const ferie = ferieData[dip.id]?.[dayYear] || [];

                        // Verifica se questo giorno fa parte di una settimana origine spostamento
                        const feriaInDay = ferie.find(f => f.date === day.date);
                        const isOrigineSposta = feriaInDay && spostamentiOrigine[`${dip.id}-${feriaInDay.weekNum}`];

                        // Lookup O(1) invece di Object.entries().some() con .find() e new Date()
                        const isDestinazioneSposta = destinazioneDateSet.has(`${dip.id}-${day.date}`);

                        // Conta destinazione spostamento
                        if (isDestinazioneSposta) {
                            count++;
                        }
                        // Conta stati normali, MA NON se è origine spostamento
                        // Include: confermata, nuova_richiesta, spostata (celeste), spostata_accordo (blu)
                        else if (!isOrigineSposta) {
                            const hasFerie = ferie.some(f =>
                                f.date === day.date && (f.stato === 'confermata' || f.stato === 'nuova_richiesta' || f.stato === 'spostata' || f.stato === 'spostata_accordo')
                            );
                            if (hasFerie) count++;
                        }
                    });
                    counters[day.date] = count;
                }
            });
        });
        return counters;
    }, [monthsStructure, filteredDipendenti, ferieData, spostamentiBozza, weeksStructure]);

    // Nascondi riga
    const handleHideRow = (dipendenteId) => {
        // Aggiorna subito lo stato locale
        setHiddenRows(prev => new Set([...prev, dipendenteId]));

        // Prova a persistere su backend (in background)
        const token = localStorage.getItem('token');
        if (token) {
            fetch(`${API_URL}/piano-ferie/hidden-rows/${dipendenteId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }).catch(error => {
                console.error('Errore nel persistere riga nascosta:', error);
            });
        }
    };

    // Riattiva riga
    const handleUnhideRow = (dipendenteId) => {
        // Aggiorna subito lo stato locale
        setHiddenRows(prev => {
            const newSet = new Set(prev);
            newSet.delete(dipendenteId);
            return newSet;
        });

        // Prova a persistere su backend (in background)
        const token = localStorage.getItem('token');
        if (token) {
            fetch(`${API_URL}/piano-ferie/hidden-rows/${dipendenteId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }).catch(error => {
                console.error('Errore nel persistere riattivazione riga:', error);
            });
        }
    };

    // Sposta ferie (drag & drop o dialog)
    // originalWeekFrom: settimana di richiesta originale (per spostata_bozza che viene ri-spostata)
    // postIdOverride: postId già noto (per ri-spostamento di spostata_bozza)
    const handleMoveFerie = async (dipendenteId, fromWeek, toWeek, sourceStato, originalWeekFrom, postIdOverride) => {
        // Se stiamo spostando una spostata_bozza, manteniamo la weekFrom originale
        const actualWeekFrom = (sourceStato === 'spostata_bozza' && originalWeekFrom) ? originalWeekFrom : fromWeek;

        // Determina l'anno reale delle settimane coinvolte
        const fromWeekData = weeksStructure.find(w => w.weekNum === fromWeek);
        const toWeekData = weeksStructure.find(w => w.weekNum === toWeek);
        const fromAnno = fromWeekData ? fromWeekData.anno : selectedYear;
        const toAnno = toWeekData ? toWeekData.anno : selectedYear;
        const actualFromAnno = (sourceStato === 'spostata_bozza' && originalWeekFrom) ?
            (weeksStructure.find(w => w.weekNum === originalWeekFrom)?.anno || selectedYear) : fromAnno;

        // Recupera il postId: usa override se disponibile, altrimenti cerca dalla settimana originale
        let postId = postIdOverride;
        if (!postId) {
            postId = getPostIdForWeek(dipendenteId, actualWeekFrom, actualFromAnno);
        }

        // Optimistic update: aggiorna la UI immediatamente prima della risposta del server
        const prevSpostamenti = { ...spostamentiBozza };
        setSpostamentiBozza(prev => {
            const newState = { ...prev };
            if (sourceStato === 'spostata_bozza') {
                delete newState[`${dipendenteId}-${fromWeek}-${fromAnno}`];
            }
            newState[`${dipendenteId}-${toWeek}-${toAnno}`] = {
                weekFrom: actualWeekFrom,
                weekTo: toWeek,
                postId: postId
            };
            return newState;
        });
        setShowMoveDialog(null);

        try {
            // Se stiamo ri-spostando una spostata_bozza, prima elimina la vecchia
            if (sourceStato === 'spostata_bozza') {
                await fetch(`${API_URL}/piano-ferie/spostamenti-bozza/${dipendenteId}/${actualWeekFrom}/${selectedYear}`, {
                    method: 'DELETE'
                });
            }

            // Salva lo spostamento come bozza nel DB
            const response = await fetch(`${API_URL}/piano-ferie/spostamenti-bozza`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    dipendenteId,
                    weekFrom: actualWeekFrom,
                    weekTo: toWeek,
                    anno: selectedYear,
                    postId: postId
                })
            });

            if (!response.ok) {
                // Rollback se il server risponde con errore
                setSpostamentiBozza(prevSpostamenti);
            }
        } catch (error) {
            console.error('Errore nel salvataggio spostamento bozza:', error);
            setSpostamentiBozza(prevSpostamenti);
        }
    };

    // Annulla uno spostamento bozza (ripristina lo stato originale)
    const handleAnnullaSpostamentoBozza = async (dipendenteId, weekFrom, weekTo) => {
        // Determina l'anno reale della settimana destinazione per la chiave frontend
        const toWeekData = weeksStructure.find(w => w.weekNum === weekTo);
        const toAnno = toWeekData ? toWeekData.anno : selectedYear;

        // Optimistic update: aggiorna la UI immediatamente
        const prevSpostamenti = { ...spostamentiBozza };
        setSpostamentiBozza(prev => {
            const newData = { ...prev };
            delete newData[`${dipendenteId}-${weekTo}-${toAnno}`];
            return newData;
        });
        setShowRecapModal(null);

        try {
            const response = await fetch(`${API_URL}/piano-ferie/spostamenti-bozza/${dipendenteId}/${weekFrom}/${selectedYear}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                setSpostamentiBozza(prevSpostamenti);
            }
        } catch (error) {
            console.error('Errore nell\'annullamento spostamento bozza:', error);
            setSpostamentiBozza(prevSpostamenti);
        }
    };

    // Logica core trasmissione spostamento (senza confirm dialog)
    const _doTrasmettiSpostamento = async (dipendenteId, weekTo) => {
        // Determina l'anno reale della settimana destinazione
        const toWeekData = weeksStructure.find(w => w.weekNum === weekTo);
        const toAnno = toWeekData ? toWeekData.anno : selectedYear;

        // Recupera weekFrom dall'oggetto spostamento (la chiave è weekTo)
        const spostamento = spostamentiBozza[`${dipendenteId}-${weekTo}-${toAnno}`];
        if (!spostamento) {
            console.error('Spostamento bozza non trovato');
            return;
        }
        const weekFrom = spostamento.weekFrom;
        const fromWeekData = weeksStructure.find(w => w.weekNum === weekFrom);
        const fromAnno = fromWeekData ? fromWeekData.anno : selectedYear;

        try {
            const response = await fetch(`${API_URL}/piano-ferie/spostamenti-bozza/trasmetti`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    dipendenteId,
                    weekFrom,
                    anno: selectedYear
                })
            });

            if (response.ok) {
                const data = await response.json();
                // Rimuovi dalla bozza locale (chiave = weekTo con anno reale)
                setSpostamentiBozza(prev => {
                    const newData = { ...prev };
                    delete newData[`${dipendenteId}-${weekTo}-${toAnno}`];
                    return newData;
                });

                // Aggiorna ferieData per mostrare come 'rifiutata' (origine) e 'spostata' (destinazione)
                setFerieData(prev => {
                    const newFerieData = { ...prev };

                    // Aggiorna anno origine
                    const dipFerieFrom = [...(newFerieData[dipendenteId]?.[fromAnno] || [])];
                    const filteredFerieFrom = dipFerieFrom.filter(f => f.weekNum !== weekFrom);
                    const mondayFrom = getMondayOfWeek(fromAnno, weekFrom);
                    for (let d = 0; d < 7; d++) {
                        const date = new Date(mondayFrom);
                        date.setDate(mondayFrom.getDate() + d);
                        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        filteredFerieFrom.push({
                            date: dateStr,
                            stato: 'rifiutata',
                            weekNum: weekFrom
                        });
                    }

                    // Aggiorna anno destinazione
                    const destAnno = toAnno;
                    const dipFerieTo = fromAnno === destAnno ? filteredFerieFrom : [...(newFerieData[dipendenteId]?.[destAnno] || [])];
                    const filteredFerieTo = fromAnno === destAnno ? dipFerieTo.filter(f => f.weekNum !== data.weekTo) : dipFerieTo.filter(f => f.weekNum !== data.weekTo);
                    const mondayTo = getMondayOfWeek(destAnno, data.weekTo);
                    for (let d = 0; d < 7; d++) {
                        const date = new Date(mondayTo);
                        date.setDate(mondayTo.getDate() + d);
                        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        filteredFerieTo.push({
                            date: dateStr,
                            stato: 'spostata',
                            weekNum: data.weekTo
                        });
                    }

                    if (fromAnno === destAnno) {
                        newFerieData[dipendenteId] = {
                            ...newFerieData[dipendenteId],
                            [fromAnno]: filteredFerieTo
                        };
                    } else {
                        newFerieData[dipendenteId] = {
                            ...newFerieData[dipendenteId],
                            [fromAnno]: filteredFerieFrom,
                            [destAnno]: filteredFerieTo
                        };
                    }
                    return newFerieData;
                });

                setShowRecapModal(null);
            }
        } catch (error) {
            console.error('Errore nella trasmissione spostamento:', error);
        }
    };

    // Trasmetti singolo spostamento a Interacta (con confirm dialog)
    const handleTrasmettiSpostamento = async (dipendenteId, weekTo) => {
        if (!window.confirm('Sei sicuro di voler trasmettere i dati?')) return;
        await _doTrasmettiSpostamento(dipendenteId, weekTo);
    };

    // Trasmetti conferma a Interacta (Nuova richiesta → Confermata)
    const handleTrasmettiConferma = async (dipendenteId, weekNum, anno) => {
        if (!window.confirm('Sei sicuro di voler trasmettere i dati?')) return;
        const annoEffettivo = anno || selectedYear;
        // Recupera il postId dalla ferieData
        const ferie = ferieData[dipendenteId]?.[annoEffettivo] || [];
        const ferieWeek = ferie.find(f => f.weekNum === weekNum && f.stato === 'nuova_richiesta');

        if (!ferieWeek?.postId) {
            console.error('PostId non trovato per questa richiesta');
            alert('Errore: PostId non disponibile per questa richiesta');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/piano-ferie/interacta/conferma`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ postId: ferieWeek.postId })
            });

            if (response.ok) {
                // Aggiorna ferieData locale per mostrare come 'confermata'
                setFerieData(prev => {
                    const newFerieData = { ...prev };
                    const dipFerie = [...(newFerieData[dipendenteId]?.[annoEffettivo] || [])];

                    // Aggiorna lo stato della settimana a 'confermata'
                    const updatedFerie = dipFerie.map(f => {
                        if (f.weekNum === weekNum && f.stato === 'nuova_richiesta') {
                            return { ...f, stato: 'confermata' };
                        }
                        return f;
                    });

                    newFerieData[dipendenteId] = {
                        ...newFerieData[dipendenteId],
                        [annoEffettivo]: updatedFerie
                    };
                    return newFerieData;
                });

                setShowRecapModal(null);
            } else {
                const errorData = await response.json();
                alert(`Errore: ${errorData.error}`);
            }
        } catch (error) {
            console.error('Errore nella trasmissione conferma:', error);
            alert('Errore nella trasmissione conferma');
        }
    };

    // Trasmetti tutti gli spostamenti a Interacta
    const handleTrasmettiTutti = async () => {
        if (!window.confirm('Sei sicuro di voler trasmettere i dati?')) return;
        try {
            const response = await fetch(`${API_URL}/piano-ferie/spostamenti-bozza/trasmetti-tutti`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ anno: selectedYear, excludeDipendentiIds: Object.keys(spostamentiBozza).map(key => parseInt(key.split('-')[0])).filter(dipId => !dipendenti.some(d => d.id === dipId)) })
            });

            if (response.ok) {
                const data = await response.json();
                // Svuota tutte le bozze locali
                setSpostamentiBozza({});
                // Ricarica i dati (in un'app reale ricaricheresti da Interacta)
                alert(`${data.count} spostamenti trasmessi con successo!`);
                // Per ora forziamo un reload della pagina per semplicità
                window.location.reload();
            }
        } catch (error) {
            console.error('Errore nella trasmissione massiva:', error);
        }
    };

    // Conta spostamenti bozza pendenti (escludi dipendenti non più attivi)
    const countSpostamentiBozza = Object.keys(spostamentiBozza).filter(key => {
        const dipId = parseInt(key.split('-')[0]);
        return dipendenti.some(d => d.id === dipId);
    }).length;

    // Apri dialog spostamento
    const handleOpenMoveDialog = (dipendenteId, weekNum, sourceStato, originalWeekFrom) => {
        setShowMoveDialog({ dipendenteId, weekNum, sourceStato, originalWeekFrom });
    };

    // Apri modale recap ferie
    const handleOpenRecapModal = (dipendenteId, weekNum, stato, anno) => {
        const dipendente = dipendenti.find(d => d.id === dipendenteId);
        const week = weeksStructure.find(w => w.weekNum === weekNum);
        if (dipendente && week) {
            // Recupera dati extra da ferieData per stati di spostamento
            const ferie = ferieData[dipendenteId]?.[selectedYear] || [];
            const ferieWeek = ferie.find(f => f.weekNum === weekNum);

            // Per spostata/spostata_accordo: settimanaOriginale contiene la settimana originale
            // Per proposta_rifiutata/proposta_rifiutata_accordo: cerco la settimana destinazione
            let settimanaOriginale = null;
            let settimanaDestinazione = null;

            if ((stato === 'spostata' || stato === 'spostata_accordo') && ferieWeek?.settimanaOriginale) {
                // Parso la settimana originale dal titolo (es. "S15 06/04/2026 - 12/04/2026")
                settimanaOriginale = ferieWeek.settimanaOriginale;
            } else if (stato === 'proposta_rifiutata' || stato === 'proposta_rifiutata_accordo') {
                // Cerco la ferie con stato spostata/spostata_accordo che ha questa settimana come origine
                const spostate = ferie.filter(f =>
                    (f.stato === 'spostata' || f.stato === 'spostata_accordo') &&
                    f.settimanaOriginale
                );
                // La settimana originale della spostata deve matchare questa weekNum
                const spostataCorrispondente = spostate.find(f => {
                    const match = f.settimanaOriginale.match(/^S(\d+)/);
                    return match && parseInt(match[1]) === weekNum;
                });
                if (spostataCorrispondente) {
                    // Trova la settimana destinazione
                    const destWeek = weeksStructure.find(w => w.weekNum === spostataCorrispondente.weekNum);
                    if (destWeek) {
                        settimanaDestinazione = `S${destWeek.weekNum} ${destWeek.startDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${destWeek.endDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
                    }
                }
            }

            setShowRecapModal({
                dipendenteId,
                weekNum,
                stato,
                anno: anno || week.anno,
                startDate: week.startDate,
                endDate: week.endDate,
                dipendenteName: `${dipendente.cognome} ${dipendente.nome}`,
                settimanaOriginale,
                settimanaDestinazione
            });
        }
    };

    // Render vista settimane
    const renderWeekView = (dipendente) => {
        // Per anno fiscale, combina ferie di entrambi gli anni
        const ferieAnnoInizio = ferieData[dipendente.id]?.[selectedYear] || [];
        const ferieAnnoFine = ferieData[dipendente.id]?.[selectedYear + 1] || [];

        // Mappa: `${weekNum}-${anno}` -> stato
        const ferieByWeekAnno = {};
        ferieAnnoInizio.forEach(f => {
            if (f.weekNum) {
                ferieByWeekAnno[`${f.weekNum}-${selectedYear}`] = f.stato;
            }
        });
        ferieAnnoFine.forEach(f => {
            if (f.weekNum) {
                ferieByWeekAnno[`${f.weekNum}-${selectedYear + 1}`] = f.stato;
            }
        });

        // Trova tutte le settimane origine per questo dipendente (per mostrare rifiutata_bozza)
        const originiSpostamenti = {};
        Object.entries(spostamentiBozza).forEach(([key, value]) => {
            if (key.startsWith(`${dipendente.id}-`)) {
                // Cerca l'anno reale della settimana origine dalla weeksStructure
                const weekOrigine = weeksStructure.find(w => w.weekNum === value.weekFrom);
                const annoOrigine = weekOrigine ? weekOrigine.anno : selectedYear;
                originiSpostamenti[`${value.weekFrom}-${annoOrigine}`] = value.weekTo;
            }
        });

        // Raggruppa settimane per mese fiscale (Apr-Mar)
        const weeksByFiscalMonth = Array(12).fill(null).map(() => []);
        weeksStructure.forEach(week => {
            const fmIdx = week.fiscalMonthIndex;
            if (fmIdx !== undefined && fmIdx >= 0 && fmIdx < 12) {
                weeksByFiscalMonth[fmIdx].push(week);
            }
        });

        return (
            <div className="weeks-grid-by-month">
                {weeksByFiscalMonth.map((weeks, fmIdx) => (
                    <div key={fmIdx} className="month-weeks-block">
                        {weeks.map((week) => {
                            const weekKey = `${week.weekNum}-${week.anno}`;

                            // Controlla se c'è uno spostamento bozza DESTINAZIONE per questa settimana
                            const spostamentoBozzaKey = `${dipendente.id}-${week.weekNum}-${week.anno}`;
                            const hasSpostamentoBozza = spostamentiBozza[spostamentoBozzaKey];

                            // Controlla se questa settimana è l'ORIGINE di uno spostamento
                            const isOrigineSpostamento = originiSpostamenti[weekKey];

                            // Stato da Interacta
                            const statoInteracta = ferieByWeekAnno[weekKey];
                            const hasBozza = bozzeData[`${dipendente.id}-${week.weekNum}-${week.anno}`];

                            // Priorità: spostamento destinazione > origine spostamento > stato Interacta > bozza
                            let stato;
                            if (hasSpostamentoBozza) {
                                stato = 'spostata_bozza';
                            } else if (isOrigineSpostamento) {
                                stato = 'rifiutata_bozza';
                            } else if (statoInteracta) {
                                stato = statoInteracta;
                            } else if (hasBozza) {
                                stato = 'bozza';
                            } else {
                                stato = null;
                            }

                            return (
                                <DraggableWeekCell
                                    key={weekKey}
                                    week={week}
                                    stato={stato}
                                    dipendenteId={dipendente.id}
                                    onDrop={(from, to, sourceStato, origWeekFrom) => handleMoveFerie(dipendente.id, from, to, sourceStato, origWeekFrom)}
                                    onClick={(wn, st) => handleOpenRecapModal(dipendente.id, wn, st, week.anno)}
                                    onEmptyClick={(wn) => handleCreateBozza(dipendente.id, wn, week.anno)}
                                    canDrag={true}
                                    originalWeekFrom={hasSpostamentoBozza?.weekFrom}
                                    onCancelMove={(dipId, weekFrom, weekTo) => handleAnnullaSpostamentoBozza(dipId, weekFrom, weekTo)}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    // Render vista giorni
    const renderDayView = (dipendente) => {
        // Unisci ferie da entrambi gli anni (anno fiscale)
        const ferieAnnoInizio = ferieData[dipendente.id]?.[selectedYear] || [];
        const ferieAnnoFine = ferieData[dipendente.id]?.[selectedYear + 1] || [];
        const ferieByDate = {};
        ferieAnnoInizio.forEach(f => { ferieByDate[f.date] = f.stato; });
        ferieAnnoFine.forEach(f => { ferieByDate[f.date] = f.stato; });

        // Trova spostamenti bozza per questo dipendente (destinazioni e origini)
        const destinazioniSpostamenti = {}; // weekNum -> true
        const originiSpostamenti = {}; // weekNum -> true
        const dipIdStr = String(dipendente.id);

        Object.entries(spostamentiBozza).forEach(([key, value]) => {
            if (key.startsWith(`${dipIdStr}-`)) {
                destinazioniSpostamenti[value.weekTo] = true;
                originiSpostamenti[value.weekFrom] = true;
            }
        });

        // Trova bozze per questo dipendente
        const bozzePerSettimana = {};
        Object.keys(bozzeData).forEach(key => {
            if (key.startsWith(`${dipIdStr}-`)) {
                const parts = key.split('-');
                const weekNum = parseInt(parts[1]);
                const anno = parseInt(parts[2]);
                // Verifica che sia nell'anno fiscale corrente
                if (anno === selectedYear || anno === selectedYear + 1) {
                    bozzePerSettimana[weekNum] = true;
                }
            }
        });

        return (
            <div className="calendar-grid">
                {monthsStructure.map((month, mIdx) => (
                    <div key={mIdx} className="month-block">
                        {month.map((day, dIdx) => {
                            if (!day.valid) {
                                return <div key={dIdx} className="calendar-cell empty" />;
                            }

                            // Calcola il numero di settimana per questo giorno
                            const dayDate = new Date(day.date + 'T00:00:00');
                            const weekNum = getWeekNumber(dayDate);

                            // Stato da Interacta
                            const statoInteracta = ferieByDate[day.date];

                            // Priorità: spostamento destinazione > origine spostamento > stato Interacta > bozza
                            let stato;
                            if (destinazioniSpostamenti[weekNum]) {
                                stato = 'spostata_bozza';
                            } else if (originiSpostamenti[weekNum]) {
                                stato = 'rifiutata_bozza';
                            } else if (statoInteracta) {
                                stato = statoInteracta;
                            } else if (bozzePerSettimana[weekNum]) {
                                stato = 'bozza';
                            } else {
                                stato = null;
                            }

                            let cellClass = 'calendar-cell';
                            if (stato) {
                                cellClass += ` ferie-${stato}`;
                            }

                            return (
                                <div
                                    key={dIdx}
                                    className={cellClass}
                                    title={stato ? STATI_FERIE[stato]?.label : ''}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    // Render vista settimane per anno specifico (storico)
    const renderWeekViewForYear = (dipendente, anno) => {
        const ferie = ferieData[dipendente.id]?.[anno] || [];
        const ferieByWeek = {};
        ferie.forEach(f => {
            if (f.weekNum) {
                ferieByWeek[f.weekNum] = f.stato;
            }
        });

        const weeksForYear = generateWeeksStructure(anno);

        // Raggruppa settimane per mese (come l'header)
        const weeksByMonth = Array(12).fill(null).map(() => []);
        weeksForYear.forEach(week => {
            weeksByMonth[week.month].push(week);
        });

        return (
            <div className="weeks-grid-by-month history">
                {weeksByMonth.map((weeks, mIdx) => (
                    <div key={mIdx} className="month-weeks-block">
                        {weeks.map((week) => {
                            const stato = ferieByWeek[week.weekNum];
                            const startDate = formatDateShort(week.startDate);
                            const endDate = formatDateShort(week.endDate);
                            const tooltipText = stato
                                ? `${STATI_FERIE[stato]?.label || stato} - S${week.weekNum}: ${startDate} - ${endDate}`
                                : `S${week.weekNum}: ${startDate} - ${endDate}`;

                            return (
                                <div
                                    key={week.weekNum}
                                    className={`week-cell ${stato ? `ferie-${stato}` : ''}`}
                                    title={tooltipText}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    // Render vista giorni per anno specifico (storico)
    const renderDayViewForYear = (dipendente, anno) => {
        const ferie = ferieData[dipendente.id]?.[anno] || [];
        const ferieByDate = {};
        ferie.forEach(f => {
            ferieByDate[f.date] = f.stato;
        });

        const monthsForYear = generateMonthsStructure(anno);

        return (
            <div className="calendar-grid history">
                {monthsForYear.map((month, mIdx) => (
                    <div key={mIdx} className="month-block">
                        {month.map((day, dIdx) => {
                            if (!day.valid) {
                                return <div key={dIdx} className="calendar-cell empty" />;
                            }

                            const stato = ferieByDate[day.date];

                            let cellClass = 'calendar-cell';
                            if (stato) {
                                cellClass += ` ferie-${stato}`;
                            }

                            return (
                                <div
                                    key={dIdx}
                                    className={cellClass}
                                    title={stato ? STATI_FERIE[stato]?.label : ''}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    // Render header settimane (ordine fiscale Apr-Mar)
    const renderWeekHeader = () => {
        // Raggruppa settimane per mese fiscale
        const weeksByFiscalMonth = Array(12).fill(null).map(() => []);
        weeksStructure.forEach(week => {
            const fmIdx = week.fiscalMonthIndex;
            if (fmIdx !== undefined && fmIdx >= 0 && fmIdx < 12) {
                weeksByFiscalMonth[fmIdx].push(week);
            }
        });

        return (
            <div className="weeks-header">
                {weeksByFiscalMonth.map((weeks, fmIdx) => (
                    <div key={fmIdx} className="month-header-block-week">
                        <div className="month-header-label-week">
                            <span className="month-name">{mesiFiscali[fmIdx]}</span>
                        </div>
                        <div className="month-header-weeks">
                            {weeks.map(week => {
                                const startDate = formatDateShort(week.startDate);
                                const endDate = formatDateShort(week.endDate);
                                return (
                                    <div
                                        key={`${week.weekNum}-${week.anno}`}
                                        className="week-header-cell"
                                        title={`S${week.weekNum} ${week.anno}: ${startDate} - ${endDate}`}
                                    >
                                        <span className="week-header-num">S{week.weekNum}</span>
                                        <span className="week-header-dates">{startDate}-{endDate}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Render counter settimane (ordine fiscale Apr-Mar)
    const renderWeekCounterRow = () => {
        const weeksByFiscalMonth = Array(12).fill(null).map(() => []);
        weeksStructure.forEach(week => {
            const fmIdx = week.fiscalMonthIndex;
            if (fmIdx !== undefined && fmIdx >= 0 && fmIdx < 12) {
                weeksByFiscalMonth[fmIdx].push(week);
            }
        });

        return (
            <div className="weeks-counter-row">
                {weeksByFiscalMonth.map((weeks, fmIdx) => (
                    <div key={fmIdx} className="month-counter-block-week">
                        {weeks.map(week => {
                            const startDate = formatDateShort(week.startDate);
                            const endDate = formatDateShort(week.endDate);
                            const weekKey = `${week.weekNum}-${week.anno}`;
                            const count = weekCounters[weekKey] || 0;
                            return (
                                <div
                                    key={weekKey}
                                    className="week-counter-cell"
                                    title={`S${week.weekNum} ${week.anno}: ${startDate} - ${endDate} (${count} dipendenti)`}
                                >
                                    <span className={`counter-value ${count > 0 ? 'has-ferie' : ''}`}>
                                        {count}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    // Render header giorni (ordine fiscale Apr-Mar)
    const renderDayHeader = () => {
        return (
            <div className="calendar-header-days">
                {monthsStructure.map((month, fmIdx) => (
                    <div key={fmIdx} className="month-header-block">
                        <div className="month-header-label">
                            <span className="month-name">{mesiFiscali[fmIdx]}</span>
                        </div>
                        <div className="month-header-days">
                            {month.map((day, dIdx) => (
                                <div
                                    key={dIdx}
                                    className={`day-header-cell ${!day.valid ? 'empty' : ''} ${day.valid && day.dayOfWeek === 0 ? 'domenica' : ''}`}
                                >
                                    {day.valid && (
                                        <>
                                            <span className="day-header-num">{day.dayOfMonth}</span>
                                            <span className="day-header-abbr">{giorniAbbr[day.dayOfWeek]}</span>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Render counter giorni
    const renderDayCounterRow = () => {
        return (
            <div className="calendar-counter-days">
                {monthsStructure.map((month, mIdx) => (
                    <div key={mIdx} className="month-counter-block">
                        {month.map((day, dIdx) => (
                            <div
                                key={dIdx}
                                className={`day-counter-cell ${!day.valid ? 'empty' : ''} ${day.valid && day.dayOfWeek === 0 ? 'domenica' : ''}`}
                            >
                                {day.valid && (
                                    <span className={`counter-value ${dailyCounters[day.date] > 0 ? 'has-ferie' : ''}`}>
                                        {dailyCounters[day.date] || 0}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="piano-ferie-container">
                <header className="page-header">
                    <div className="header-content">
                        <button className="back-button" onClick={() => navigate('/produttivita')}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 12H5M12 19l-7-7 7-7"/>
                            </svg>
                            Indietro
                        </button>
                        <div className="header-title">
                            <div className="header-title-row">
                                <h2>Piano Ferie</h2>
                                <button
                                    className="info-button-modern"
                                    onClick={() => selectedTab === 'Assegnazione' ? setShowInfoAssegnazioneNew(true) : setShowInfo(true)}
                                    title="Informazioni"
                                >
                                    <span className="info-icon-text">i</span>
                                </button>
                            </div>
                            <p className="header-subtitle">Visualizzazione e gestione delle ferie pianificate</p>
                        </div>
                    </div>
                </header>

                {/* Tab di navigazione */}
                <FiltroCategorie
                    categories={['Pianificazione', 'Assegnazione', 'Piano Ferie Quotidiano']}
                    filteredCategory={setSelectedTab}
                    customCss={"reportistica-section"}
                />

                {/* Tab Pianificazione */}
                {selectedTab === 'Pianificazione' && (
                <div className="tab-content-wrapper">
                {/* Loading e Errore */}
                {loadingData && (
                    <Lf23Loader />
                )}

                {errorData && (
                    <div className="error-container" style={{
                        padding: '20px',
                        margin: '20px',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #ef4444',
                        borderRadius: '8px',
                        color: '#b91c1c'
                    }}>
                        <strong>Errore nel caricamento:</strong> {errorData}
                    </div>
                )}

                {/* Modal Informazioni */}
                {showInfo && (
                    <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <div className="modal-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                        <line x1="16" y1="2" x2="16" y2="6"></line>
                                        <line x1="8" y1="2" x2="8" y2="6"></line>
                                        <line x1="3" y1="10" x2="21" y2="10"></line>
                                    </svg>
                                </div>
                                <h3>Informazioni Pianificazione</h3>
                                <button className="modal-close" onClick={() => setShowInfo(false)}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="modal-section">
                                    <h4>Regole ferie</h4>
                                    <p>
                                        Ogni dipendente deve fissare <strong>4 settimane</strong> ({GIORNI_FERIE_ANNUALI} giorni) di ferie all'anno (dal lunedì alla domenica).
                                        Le richieste possono essere fatte solo a blocchi settimanali.
                                    </p>
                                </div>
                                <div className="modal-section">
                                    <h4>Colonne tabella</h4>
                                    <ul>
                                        <li><strong>Dipendente</strong> - Nome e cognome del dipendente</li>
                                        <li><strong>Telefono</strong> - Cellulare e telefono fisso dell'operatore</li>
                                        <li><strong>Contratto</strong> - Tipologia contrattuale</li>
                                        <li><strong>Assegnazione</strong> - Assegnazione lavorativa corrente</li>
                                        <li><strong>Richieste</strong> - Giorni di ferie richiesti su {GIORNI_FERIE_ANNUALI} totali</li>
                                        <li><strong>Confermate</strong> - Giorni di ferie confermati su {GIORNI_FERIE_ANNUALI} totali</li>
                                        <li><strong>Richieste Ferie</strong> - Dettaglio delle singole settimane richieste con stato</li>
                                    </ul>
                                </div>
                                <div className="modal-section">
                                    <h4>Legenda colori badges</h4>
                                    <ul>
                                        {Object.entries(STATI_FERIE)
                                            .filter(([key]) => ['confermata', 'nuova_richiesta', 'rifiutata', 'rifiutata_accordo', 'spostata', 'spostata_accordo', 'bozza'].includes(key))
                                            .map(([key, value]) => (
                                            <li key={key}>
                                                <span className={`legend-dot ${key}`} style={{ background: value.color }}></span>
                                                <strong>{value.label}</strong> - {value.desc}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="modal-section">
                                    <h4>Filtri stato richieste</h4>
                                    <ul>
                                        <li><strong>Nessuna richiesta</strong> - Dipendenti con 0 giorni richiesti</li>
                                        <li><strong>Richieste parziali</strong> - Almeno 1 richiesta ma meno di {GIORNI_FERIE_ANNUALI} giorni</li>
                                        <li><strong>Richieste complete</strong> - Tutti i {GIORNI_FERIE_ANNUALI} giorni richiesti</li>
                                        <li><strong>Nessuna conferma</strong> - Dipendenti con 0 giorni confermati</li>
                                        <li><strong>Conferme parziali</strong> - Almeno 1 conferma ma meno di {GIORNI_FERIE_ANNUALI} giorni</li>
                                        <li><strong>Tutti</strong> - Mostra tutti i dipendenti</li>
                                        <li><strong>In errore</strong> - Dipendenti che superano i limiti consentiti</li>
                                    </ul>
                                </div>
                                <div className="modal-section">
                                    <h4>Nota</h4>
                                    <p>
                                        Questa vista è di sola consultazione. Per modificare o spostare le richieste ferie, utilizzare la tab "Assegnazione".
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!loadingData && (
                <div className="content-area">
                    {/* Top Controls */}
                    <div className="top-controls">
                        <div className="controls-left">
                            <button
                                className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                                </svg>
                                {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                            </button>

                            <SearchInput
                                onSearch={handleSearch}
                                onClear={handleClearSearch}
                            />
                        </div>

                        <div className="controls-right">
                            <button
                                className="btn-export-excel"
                                onClick={handleExportExcelNew}
                                disabled={filteredDipendentiNew.length === 0}
                                title="Esporta in Excel"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Esporta Excel
                            </button>
                            {hiddenDipendenti.length > 0 && (
                                <button
                                    className={`toggle-hidden-btn ${showHiddenSection ? 'active' : ''}`}
                                    onClick={() => setShowHiddenSection(!showHiddenSection)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                    </svg>
                                    Nascosti ({hiddenDipendenti.length})
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filtri */}
                    {showFilters && (
                        <div className="filters-panel">
                            <div className="filters-grid-compact">
                                <div className="filter-item">
                                    <label>Cantiere</label>
                                    <Select
                                        isMulti
                                        closeMenuOnSelect={false}
                                        classNamePrefix="piano-ferie-select"
                                        value={filtroCanitere}
                                        options={cantiereOptions}
                                        onChange={(selected) => setFiltroCantiere(selected || [])}
                                        isSearchable={true}
                                        placeholder="Tutti i cantieri"
                                    />
                                </div>
                                <div className="filter-item">
                                    <label>Commessa</label>
                                    <Select
                                        isMulti
                                        closeMenuOnSelect={false}
                                        classNamePrefix="piano-ferie-select"
                                        value={filtroCommessa}
                                        options={commessaOptions}
                                        onChange={(selected) => setFiltroCommessa(selected || [])}
                                        isSearchable={true}
                                        placeholder="Tutte le commesse"
                                    />
                                </div>
                                <div className="filter-item">
                                    <label>Assegnazione</label>
                                    <Select
                                        isMulti
                                        closeMenuOnSelect={false}
                                        classNamePrefix="piano-ferie-select"
                                        value={filtroAssegnazione}
                                        options={assegnazioneOptions}
                                        onChange={(selected) => setFiltroAssegnazione(selected || [])}
                                        isSearchable={true}
                                        placeholder="Tutte le assegnazioni"
                                    />
                                </div>
                                <div className="filter-item">
                                    <label>Stato Richieste</label>
                                    <Select
                                        classNamePrefix="piano-ferie-select"
                                        value={{
                                            value: filtroStatoNew,
                                            label: filtroStatoNew === 'tutti' ? 'Tutti' :
                                                   filtroStatoNew === 'errore' ? 'In errore' :
                                                   filtroStatoNew === 'nessuna_richiesta' ? 'Nessuna richiesta' :
                                                   filtroStatoNew === 'richieste_parziali' ? 'Richieste parziali' :
                                                   filtroStatoNew === 'richieste_complete' ? 'Richieste complete' :
                                                   filtroStatoNew === 'nessuna_conferma' ? 'Nessuna conferma' :
                                                   filtroStatoNew === 'conferme_parziali' ? 'Conferme parziali' :
                                                   'Con bozze'
                                        }}
                                        options={[
                                            { value: 'tutti', label: 'Tutti' },
                                            { value: 'nessuna_richiesta', label: 'Nessuna richiesta' },
                                            { value: 'richieste_parziali', label: 'Richieste parziali' },
                                            { value: 'richieste_complete', label: 'Richieste complete' },
                                            { value: 'nessuna_conferma', label: 'Nessuna conferma' },
                                            { value: 'conferme_parziali', label: 'Conferme parziali' },
                                            { value: 'con_bozze', label: 'Con bozze' },
                                            { value: 'errore', label: 'In errore' }
                                        ]}
                                        onChange={(selected) => setFiltroStatoNew(selected?.value || 'tutti')}
                                        isSearchable={false}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Sezione righe nascoste */}
                    {showHiddenSection && hiddenDipendenti.length > 0 && (
                        <div className="hidden-section">
                            <h4>Dipendenti nascosti</h4>
                            <div className="hidden-list">
                                {hiddenDipendenti.map(dip => (
                                    <div key={dip.id} className="hidden-item">
                                        <span>{dip.cognome} {dip.nome}</span>
                                        <button
                                            className="unhide-btn"
                                            onClick={() => handleUnhideRow(dip.id)}
                                            title="Riattiva"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                <circle cx="12" cy="12" r="3"></circle>
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Stats - contatore sintetico */}
                    <div className="stats-grid-compact">
                        <div className="stat-card-compact stat-primary">
                            <div className="stat-icon-compact">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                            </div>
                            <div className="stat-content-compact">
                                <p className="stat-label-compact">Dipendenti</p>
                                <p className="stat-value-compact">{filteredDipendentiNew.length}</p>
                            </div>
                        </div>
                        <div className="stat-card-compact stat-success">
                            <div className="stat-icon-compact">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                            </div>
                            <div className="stat-content-compact">
                                <p className="stat-label-compact">Ferie Complete</p>
                                <p className="stat-value-compact">
                                    {filteredDipendentiNew.filter(d => getStats(d.id, selectedYear).giorniConfermati >= GIORNI_FERIE_ANNUALI).length}
                                </p>
                            </div>
                        </div>
                        <div className="stat-card-compact stat-warning">
                            <div className="stat-icon-compact">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                            </div>
                            <div className="stat-content-compact">
                                <p className="stat-label-compact">Richieste Complete</p>
                                <p className="stat-value-compact">
                                    {filteredDipendentiNew.filter(d => getStats(d.id, selectedYear).giorniRichiesti >= GIORNI_FERIE_ANNUALI).length}
                                </p>
                            </div>
                        </div>
                        <div className="stat-card-compact stat-danger">
                            <div className="stat-icon-compact">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                            </div>
                            <div className="stat-content-compact">
                                <p className="stat-label-compact">In Errore</p>
                                <p className="stat-value-compact">
                                    {filteredDipendentiNew.filter(d => getStats(d.id, selectedYear).superaLimite).length}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Tabella dipendenti */}
                    <div className="fullscreen-btn-row-pf">
                        <button
                            className="fullscreen-btn-pf"
                            onClick={() => setIsFullscreenTable(!isFullscreenTable)}
                            title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isFullscreenTable ? (
                                    <>
                                        <polyline points="4 14 10 14 10 20"></polyline>
                                        <polyline points="20 10 14 10 14 4"></polyline>
                                        <line x1="14" y1="10" x2="21" y2="3"></line>
                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                    </>
                                ) : (
                                    <>
                                        <polyline points="15 3 21 3 21 9"></polyline>
                                        <polyline points="9 21 3 21 3 15"></polyline>
                                        <line x1="21" y1="3" x2="14" y2="10"></line>
                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                    </>
                                )}
                            </svg>
                        </button>
                    </div>
                    <div className={`pianificazione-new-table-wrapper${isFullscreenTable ? ' fullscreen-table-pf' : ''}`} onScroll={handleScrollBottom}>
                        <RotateDevice active={isFullscreenTable} />
                        {isFullscreenTable && (
                            <div className="fullscreen-exit-bar-pf">
                                <button
                                    className="fullscreen-btn-pf"
                                    onClick={() => setIsFullscreenTable(false)}
                                    title="Esci da tutto schermo"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="4 14 10 14 10 20"></polyline>
                                        <polyline points="20 10 14 10 14 4"></polyline>
                                        <line x1="14" y1="10" x2="21" y2="3"></line>
                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                    </svg>
                                </button>
                            </div>
                        )}
                        <table className="pianificazione-new-table">
                            <thead>
                                <tr>
                                    <th>Dipendente</th>
                                    <th>Telefono</th>
                                    <th>Contratto</th>
                                    <th>Assegnazione</th>
                                    <th>Richieste</th>
                                    <th>Confermate</th>
                                    <th>Richieste Ferie</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleDipendentiNew.map(dipendente => {
                                    const stats = getStats(dipendente.id, selectedYear);
                                    // Raccoglie tutte le ferie per l'anno fiscale (annoInizio + annoFine)
                                    const ferieAnnoInizio = ferieData[dipendente.id]?.[selectedYear] || [];
                                    const ferieAnnoFine = ferieData[dipendente.id]?.[selectedYear + 1] || [];
                                    const tutteFerie = [...ferieAnnoInizio, ...ferieAnnoFine];
                                    // Raggruppa per settimana (prende lo stato più recente per weekNum)
                                    const feriePerSettimana = {};
                                    tutteFerie.forEach(f => {
                                        const key = `${f.weekNum}-${f.date?.substring(0,4)}`;
                                        if (!feriePerSettimana[key]) {
                                            feriePerSettimana[key] = f;
                                        }
                                    });

                                    // Integra spostamenti bozza (destinazioni e origini)
                                    const originiSpostamentiPian = {};
                                    Object.entries(spostamentiBozza).forEach(([key, value]) => {
                                        if (key.startsWith(`${dipendente.id}-`)) {
                                            const weekOrigine = weeksStructure.find(w => w.weekNum === value.weekFrom);
                                            const annoOrigine = weekOrigine ? weekOrigine.anno : selectedYear;
                                            originiSpostamentiPian[`${value.weekFrom}-${annoOrigine}`] = value.weekTo;
                                        }
                                    });

                                    weeksStructure.forEach(week => {
                                        const weekKey = `${week.weekNum}-${week.anno}`;
                                        if (feriePerSettimana[weekKey]) return; // priorità a stato Interacta

                                        const spostKey = `${dipendente.id}-${week.weekNum}-${week.anno}`;
                                        const hasSpostDest = spostamentiBozza[spostKey];
                                        const isOrigine = originiSpostamentiPian[weekKey];
                                        const hasBozza = bozzeData[`${dipendente.id}-${week.weekNum}-${week.anno}`];

                                        let stato = null;
                                        if (hasSpostDest) stato = 'spostata_bozza';
                                        else if (isOrigine) stato = 'rifiutata_bozza';
                                        else if (hasBozza) stato = 'bozza';

                                        if (stato) {
                                            feriePerSettimana[weekKey] = {
                                                weekNum: week.weekNum,
                                                stato,
                                                date: `${week.anno}-01-01`
                                            };
                                        }
                                    });

                                    const ferieUniche = Object.values(feriePerSettimana)
                                        .sort((a, b) => {
                                            const annoA = parseInt(a.date?.substring(0,4) || '0');
                                            const annoB = parseInt(b.date?.substring(0,4) || '0');
                                            if (annoA !== annoB) return annoA - annoB;
                                            return a.weekNum - b.weekNum;
                                        });

                                    return (
                                        <tr key={dipendente.id} className={stats.superaLimite ? 'alert-row' : ''}>
                                            <td className="td-dipendente">
                                                {dipendente.cognome} {dipendente.nome}
                                                {stats.superaLimite && (
                                                    <span
                                                        className="alert-badge"
                                                        onMouseEnter={(e) => {
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setAlertTooltip({ x: rect.left + rect.width / 2, y: rect.top, errors: getAlertErrors(stats) });
                                                        }}
                                                        onMouseLeave={() => setAlertTooltip(null)}
                                                    >!</span>
                                                )}
                                            </td>
                                            <td className="td-telefono">
                                                {dipendente.cellulare || dipendente.telFisso ? (
                                                    <>
                                                        {dipendente.cellulare && <span>{dipendente.cellulare}</span>}
                                                        {dipendente.cellulare && dipendente.telFisso && <br />}
                                                        {dipendente.telFisso && <span>{dipendente.telFisso}</span>}
                                                    </>
                                                ) : '—'}
                                            </td>
                                            <td className="td-contratto">
                                                {dipendente.contratto || '—'}
                                            </td>
                                            <td className="td-assegnazione">
                                                <span className="badge-assegnazione">{dipendente.assegnazione_dett}</span>
                                            </td>
                                            <td className="td-counter">
                                                <span className={`counter-value ${stats.giorniRichiesti >= GIORNI_FERIE_ANNUALI ? 'green' : 'red'}`}>
                                                    {stats.giorniRichiesti}/{GIORNI_FERIE_ANNUALI}
                                                </span>
                                            </td>
                                            <td className="td-counter">
                                                <span className={`counter-value ${stats.giorniConfermati >= GIORNI_FERIE_ANNUALI ? 'green' : 'red'}`}>
                                                    {stats.giorniConfermati}/{GIORNI_FERIE_ANNUALI}
                                                </span>
                                            </td>
                                            <td className="td-ferie-chips">
                                                {ferieUniche.length > 0 ? (
                                                    <div className="ferie-chips-container">
                                                        {ferieUniche.map(f => {
                                                            const statoInfo = STATI_FERIE[f.stato] || { label: f.stato, color: '#94A3B8' };
                                                            const weekInfo = weeksStructure.find(w => w.weekNum === f.weekNum && w.anno === parseInt(f.date?.substring(0,4)));
                                                            const startStr = weekInfo?.startDate?.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) || '';
                                                            const endStr = weekInfo?.endDate?.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) || '';
                                                            return (
                                                                <div
                                                                    key={`${f.weekNum}-${f.date?.substring(0,4)}`}
                                                                    className="ferie-chip"
                                                                    style={{ backgroundColor: statoInfo.color + '20', borderColor: statoInfo.color }}
                                                                    title={statoInfo.desc || statoInfo.label}
                                                                >
                                                                    <span className="ferie-chip-week" style={{ color: statoInfo.color }}>S{f.weekNum}</span>
                                                                    <span className="ferie-chip-dates">{startStr} - {endStr}</span>
                                                                    <span className="ferie-chip-stato" style={{ color: statoInfo.color }}>{statoInfo.label}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <span className="no-ferie">Nessuna richiesta</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {visibleRowsCount < filteredDipendentiNew.length && (
                            <div className="load-more-indicator" style={{ textAlign: 'center', padding: '12px' }}>
                                Scorri per caricare altri {Math.min(ROWS_PER_PAGE, filteredDipendentiNew.length - visibleRowsCount)} dipendenti...
                            </div>
                        )}
                    </div>

                    {/* Empty State */}
                    {filteredDipendentiNew.length === 0 && (
                        <div className="empty-state">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="M16 16s-1.5-2-4-2-4 2-4 2"></path>
                                <line x1="9" y1="9" x2="9.01" y2="9"></line>
                                <line x1="15" y1="9" x2="15.01" y2="9"></line>
                            </svg>
                            <h3>Nessun dipendente trovato</h3>
                            <p>Prova a modificare i filtri di ricerca</p>
                        </div>
                    )}
                </div>
                )}
                </div>
                )}

                {/* Tab Assegnazione */}
                {selectedTab === 'Assegnazione' && (
                    <div className="tab-content-wrapper">
                        {/* Loading e Errore */}
                        {loadingData && (
                            <Lf23Loader />
                        )}

                        {errorData && (
                            <div className="error-container" style={{
                                padding: '20px',
                                margin: '20px',
                                backgroundColor: '#fee2e2',
                                border: '1px solid #ef4444',
                                borderRadius: '8px',
                                color: '#b91c1c'
                            }}>
                                <strong>Errore nel caricamento:</strong> {errorData}
                            </div>
                        )}

                        {/* Modale informativa Assegnazione */}
                        {showInfoAssegnazioneNew && (
                            <div className="modal-overlay" onClick={() => setShowInfoAssegnazioneNew(false)}>
                                <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                                    <div className="modal-header">
                                        <div className="modal-icon">
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                                <circle cx="9" cy="7" r="4"></circle>
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                            </svg>
                                        </div>
                                        <h3>Informazioni Assegnazione</h3>
                                        <button className="modal-close" onClick={() => setShowInfoAssegnazioneNew(false)}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="modal-body">
                                        <div className="modal-section">
                                            <h4>Struttura gerarchica</h4>
                                            <p>
                                                La vista organizza i dipendenti in una gerarchia <strong>Commessa &rarr; Assegnazione &rarr; Dipendenti</strong>.
                                                Clicca sulla freccia a sinistra di ogni assegnazione per espandere e visualizzare i singoli operatori.
                                            </p>
                                        </div>
                                        <div className="modal-section">
                                            <h4>Editing dipendente</h4>
                                            <p>
                                                A livello di riga dipendente, puoi <strong>creare bozze</strong> cliccando su una cella vuota,
                                                <strong>spostare richieste</strong> trascinando una cella su un'altra settimana, e
                                                <strong>confermare o eliminare</strong> cliccando su una cella con stato.
                                            </p>
                                        </div>
                                        <div className="modal-section">
                                            <h4>Legenda colori celle</h4>
                                            <ul>
                                                {Object.entries(STATI_FERIE).map(([key, value]) => (
                                                    <li key={key}>
                                                        <span className={`legend-dot ${key}`} style={{ background: value.color }}></span>
                                                        <strong>{value.label}</strong> - {value.desc}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="modal-section">
                                            <h4>Drag & Drop</h4>
                                            <p>
                                                Puoi trascinare le celle con stato "Nuova richiesta" o "Spostata (bozza)" su una settimana vuota per spostare la richiesta.
                                                Per annullare uno spostamento, trascina la cella azzurra sulla cella rossa originale.
                                            </p>
                                        </div>
                                        <div className="modal-section">
                                            <h4>Celle assegnazione (riga superiore)</h4>
                                            <p>Ogni cella mostra il rapporto <strong>richieste / max consentite</strong> per quella settimana.</p>
                                            <ul>
                                                <li>
                                                    <span className="legend-dot" style={{ background: '#FECACA', border: '1px solid #F87171' }}></span>
                                                    <strong>Rosso</strong> - Richieste superiori al massimo consentito
                                                </li>
                                                <li>
                                                    <span className="legend-dot" style={{ background: '#D1FAE5', border: '1px solid #34D399' }}></span>
                                                    <strong>Verde</strong> - Richieste pari al massimo consentito
                                                </li>
                                                <li>
                                                    <span className="legend-dot" style={{ background: '#F1F5F9', border: '1px solid #CBD5E1' }}></span>
                                                    <strong>Grigio</strong> - Richieste inferiori al massimo consentito
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="modal-section">
                                            <h4>Filtri</h4>
                                            <p>
                                                Usa i filtri per restringere la vista per <strong>Cantiere</strong>, <strong>Commessa</strong> o <strong>Assegnazione</strong>.
                                                Il filtro <strong>Criticità</strong> mostra solo le assegnazioni che hanno almeno una settimana in rosso.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!loadingData && (
                        <div className={`content-area${isFullscreenAssegnazioni ? ' fullscreen-assegnazioni' : ''}`}>
                            <RotateDevice active={isFullscreenAssegnazioni} />
                            {/* Top Controls */}
                            <div className="top-controls">
                                <div className="controls-left">
                                    {!isFullscreenAssegnazioni && (
                                        <button
                                            className={`toggle-filters-btn ${showFiltersAssegnazioni ? 'active' : ''}`}
                                            onClick={() => setShowFiltersAssegnazioni(!showFiltersAssegnazioni)}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                                            </svg>
                                            {showFiltersAssegnazioni ? 'Nascondi Filtri' : 'Mostra Filtri'}
                                        </button>
                                    )}
                                </div>
                                <div className="controls-right">
                                    <div className="assegnazioni-stats">
                                        <span className="stat-badge">
                                            <strong>{filteredCommesseForAssegnazioneNew.length}</strong> Cantieri
                                        </span>
                                        <span className="stat-badge">
                                            <strong>{filteredCommesseForAssegnazioneNew.reduce((sum, g) => sum + g.totaleDipendenti, 0)}</strong> Operatori
                                        </span>
                                    </div>
                                    {countSpostamentiBozza > 0 && (
                                        <button
                                            className="btn-trasmetti-tutte"
                                            onClick={() => {
                                                setFiltroCantiereRiepilogo([]);
                                                setFiltroCommessaRiepilogo([]);
                                                setFiltroAssegnazioneRiepilogo([]);
                                                setSearchRiepilogo('');
                                                setShowRiepilogoSpostamenti(true);
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                            </svg>
                                            Trasmetti tutte ({countSpostamentiBozza})
                                        </button>
                                    )}
                                    {hiddenDipendenti.length > 0 && (
                                        <button
                                            className={`toggle-hidden-btn ${showHiddenSection ? 'active' : ''}`}
                                            onClick={() => setShowHiddenSection(!showHiddenSection)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"></path>
                                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"></path>
                                                <line x1="1" y1="1" x2="23" y2="23"></line>
                                            </svg>
                                            Nascosti ({hiddenDipendenti.length})
                                        </button>
                                    )}
                                    <button
                                        className="info-button-modern"
                                        onClick={() => setShowInfoAssegnazioneNew(true)}
                                        title="Informazioni"
                                        style={{ marginLeft: '8px' }}
                                    >
                                        <span className="info-icon-text">i</span>
                                    </button>
                                    <button
                                        className="info-button-modern"
                                        onClick={() => setIsFullscreenAssegnazioni(!isFullscreenAssegnazioni)}
                                        title={isFullscreenAssegnazioni ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                        style={{ marginLeft: '8px' }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            {isFullscreenAssegnazioni ? (
                                                <>
                                                    <polyline points="4 14 10 14 10 20"></polyline>
                                                    <polyline points="20 10 14 10 14 4"></polyline>
                                                    <line x1="14" y1="10" x2="21" y2="3"></line>
                                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                                </>
                                            ) : (
                                                <>
                                                    <polyline points="15 3 21 3 21 9"></polyline>
                                                    <polyline points="9 21 3 21 3 15"></polyline>
                                                    <line x1="21" y1="3" x2="14" y2="10"></line>
                                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                                </>
                                            )}
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Filtri */}
                            {showFiltersAssegnazioni && !isFullscreenAssegnazioni && (
                                <div className="filters-panel">
                                    <div className="filters-grid-compact">
                                        <div className="filter-item">
                                            <label>Cantiere</label>
                                            <Select
                                                isMulti
                                                closeMenuOnSelect={false}
                                                classNamePrefix="piano-ferie-select"
                                                value={filtroCantiereAssegnazioni}
                                                options={cantiereOptions}
                                                onChange={(selected) => setFiltroCantiereAssegnazioni(selected || [])}
                                                isSearchable={true}
                                                placeholder="Tutti i cantieri"
                                            />
                                        </div>
                                        <div className="filter-item">
                                            <label>Commessa</label>
                                            <Select
                                                isMulti
                                                closeMenuOnSelect={false}
                                                classNamePrefix="piano-ferie-select"
                                                value={filtroCommessaAssegnazioni}
                                                options={commessaOptions}
                                                onChange={(selected) => setFiltroCommessaAssegnazioni(selected || [])}
                                                isSearchable={true}
                                                placeholder="Tutte le commesse"
                                            />
                                        </div>
                                        <div className="filter-item">
                                            <label>Assegnazione</label>
                                            <Select
                                                isMulti
                                                closeMenuOnSelect={false}
                                                classNamePrefix="piano-ferie-select"
                                                value={filtroAssegnazioneAssegnazioni}
                                                options={assegnazioneGroupOptions}
                                                onChange={(selected) => setFiltroAssegnazioneAssegnazioni(selected || [])}
                                                isSearchable={true}
                                                placeholder="Tutte le assegnazioni"
                                            />
                                        </div>
                                        <div className="filter-item">
                                            <label>Criticità</label>
                                            <Select
                                                classNamePrefix="piano-ferie-select"
                                                value={{ value: filtroCriticita, label: filtroCriticita ? 'Solo con criticità' : 'Tutte' }}
                                                options={[
                                                    { value: false, label: 'Tutte' },
                                                    { value: true, label: 'Solo con criticità' }
                                                ]}
                                                onChange={(selected) => setFiltroCriticita(selected?.value || false)}
                                                isSearchable={false}
                                            />
                                        </div>
                                        <div className="filter-item">
                                            <label>Stato Richieste</label>
                                            <Select
                                                classNamePrefix="piano-ferie-select"
                                                value={{
                                                    value: filtroStatoAssegnazioneNew,
                                                    label: filtroStatoAssegnazioneNew === 'tutti' ? 'Tutti' :
                                                           filtroStatoAssegnazioneNew === 'errore' ? 'In errore' :
                                                           filtroStatoAssegnazioneNew === 'nessuna_richiesta' ? 'Nessuna richiesta' :
                                                           filtroStatoAssegnazioneNew === 'richieste_parziali' ? 'Richieste parziali' :
                                                           filtroStatoAssegnazioneNew === 'richieste_complete' ? 'Richieste complete' :
                                                           filtroStatoAssegnazioneNew === 'nessuna_conferma' ? 'Nessuna conferma' :
                                                           filtroStatoAssegnazioneNew === 'conferme_parziali' ? 'Conferme parziali' :
                                                           'Con bozze'
                                                }}
                                                options={[
                                                    { value: 'tutti', label: 'Tutti' },
                                                    { value: 'nessuna_richiesta', label: 'Nessuna richiesta' },
                                                    { value: 'richieste_parziali', label: 'Richieste parziali' },
                                                    { value: 'richieste_complete', label: 'Richieste complete' },
                                                    { value: 'nessuna_conferma', label: 'Nessuna conferma' },
                                                    { value: 'conferme_parziali', label: 'Conferme parziali' },
                                                    { value: 'con_bozze', label: 'Con bozze' },
                                                    { value: 'errore', label: 'In errore' }
                                                ]}
                                                onChange={(selected) => setFiltroStatoAssegnazioneNew(selected?.value || 'tutti')}
                                                isSearchable={false}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Sezione righe nascoste - Assegnazione */}
                            {showHiddenSection && hiddenDipendenti.length > 0 && (
                                <div className="hidden-section">
                                    <h4>Dipendenti nascosti</h4>
                                    <div className="hidden-list">
                                        {hiddenDipendenti.map(dip => (
                                            <div key={dip.id} className="hidden-item">
                                                <span>{dip.cognome} {dip.nome}</span>
                                                <button
                                                    className="unhide-btn"
                                                    onClick={() => handleUnhideRow(dip.id)}
                                                    title="Riattiva"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Stats KPI globali - Assegnazione */}
                            {!isFullscreenAssegnazioni && (() => {
                                const allDip = filteredCommesseForAssegnazioneNew.flatMap(g => g.assegnazioni.flatMap(a => a.dipendenti));
                                return (
                                    <div className="stats-grid-compact">
                                        <div className="stat-card-compact stat-primary">
                                            <div className="stat-icon-compact">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="9" cy="7" r="4"></circle>
                                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                                </svg>
                                            </div>
                                            <div className="stat-content-compact">
                                                <p className="stat-label-compact">Dipendenti</p>
                                                <p className="stat-value-compact">{allDip.length}</p>
                                            </div>
                                        </div>
                                        <div className="stat-card-compact stat-success">
                                            <div className="stat-icon-compact">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                                </svg>
                                            </div>
                                            <div className="stat-content-compact">
                                                <p className="stat-label-compact">Ferie Complete</p>
                                                <p className="stat-value-compact">
                                                    {allDip.filter(d => getStats(d.id, selectedYear).giorniConfermati >= GIORNI_FERIE_ANNUALI).length}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="stat-card-compact stat-warning">
                                            <div className="stat-icon-compact">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <polyline points="12 6 12 12 16 14"></polyline>
                                                </svg>
                                            </div>
                                            <div className="stat-content-compact">
                                                <p className="stat-label-compact">Richieste Complete</p>
                                                <p className="stat-value-compact">
                                                    {allDip.filter(d => getStats(d.id, selectedYear).giorniRichiesti >= GIORNI_FERIE_ANNUALI).length}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="stat-card-compact stat-danger">
                                            <div className="stat-icon-compact">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                                </svg>
                                            </div>
                                            <div className="stat-content-compact">
                                                <p className="stat-label-compact">In Errore</p>
                                                <p className="stat-value-compact">
                                                    {allDip.filter(d => getStats(d.id, selectedYear).superaLimite).length}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Calendar Assegnazione - Struttura gerarchica con editing */}
                            <div className="calendar-wrapper view-weeks assegnazioni-view">
                                <div className="assegnazioni-unified-scroll">
                                  <div className="assegnazioni-unified-inner">
                                    {/* Header row (sticky top) */}
                                    <div className="assegnazioni-header-row">
                                        <div className="assegnazioni-left-cell sticky-left">
                                            <div className="header-btn-placeholder-small"></div>
                                            <div className="calendar-commessa-header">Cantiere / Assegnazione</div>
                                            <div className="calendar-operatori-header">Op. Rich.</div>
                                            <div className="calendar-operatori-header">Op. Disp.</div>
                                        </div>
                                        <div className="assegnazioni-right-cell">
                                            {renderWeekHeader()}
                                        </div>
                                    </div>

                                    {/* Body rows */}
                                    {filteredCommesseForAssegnazioneNew.map(cantiereGroup => {
                                        const weeksByFiscalMonth = Array(12).fill(null).map(() => []);
                                        weeksStructure.forEach(week => {
                                            const fmIdx = week.fiscalMonthIndex;
                                            if (fmIdx !== undefined && fmIdx >= 0 && fmIdx < 12) {
                                                weeksByFiscalMonth[fmIdx].push(week);
                                            }
                                        });

                                        return (
                                            <div className="cantiere-group-container" key={cantiereGroup.cantiere}>
                                                {/* Commessa header row */}
                                                <div className="assegnazioni-row commessa-header-row">
                                                    <div className="assegnazioni-left-cell sticky-left commessa-header-bg">
                                                        <span className="commessa-nome">{cantiereGroup.cantiere}</span>
                                                        <span className="commessa-totale">{cantiereGroup.totaleDipendenti} op.</span>
                                                    </div>
                                                    <div className="assegnazioni-right-cell commessa-header-bg"></div>
                                                </div>

                                                {/* Assegnazioni del cantiere */}
                                                {cantiereGroup.assegnazioni.map(assegnazioneGroup => {
                                                    const assegnazioneKey = `${cantiereGroup.cantiere}|||${assegnazioneGroup.assegnazione}`;
                                                    const isAssegnazioneExpanded = expandedAssegnazioni.has(assegnazioneKey);
                                                    const totaleOp = assegnazioneGroup.dipendenti.length;
                                                    const operatoriRichiestiDefault = getOperatoriRichiestiDefault(assegnazioneKey, totaleOp);
                                                    const isDefaultEdit = isDefaultEdited(assegnazioneKey);
                                                    const isEditingDefault = editingCell?.assegnazioneKey === assegnazioneKey && editingCell?.type === 'default';
                                                    return (
                                                        <div className="assegnazione-group-container" key={assegnazioneKey}>
                                                            {/* Assegnazione row - left info + right week cells */}
                                                            <div className={`assegnazioni-row assegnazione-row${isAssegnazioneExpanded ? ' sticky-assegnazione' : ''}`}>
                                                                <div className="assegnazioni-left-cell sticky-left assegnazione-bg">
                                                                    <button
                                                                        className={`expand-btn ${isAssegnazioneExpanded ? 'expanded' : ''}`}
                                                                        onClick={() => toggleExpandAssegnazione(assegnazioneKey)}
                                                                        title={isAssegnazioneExpanded ? 'Nascondi dipendenti' : 'Mostra dipendenti'}
                                                                    >
                                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                            <polyline points={isAssegnazioneExpanded ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
                                                                        </svg>
                                                                    </button>
                                                                    <div className="calendar-assegnazione-detail">
                                                                        <span className="badge-assegnazione">{assegnazioneGroup.assegnazione}</span>
                                                                    </div>
                                                                    <div
                                                                        className={`calendar-operatori editable ${isDefaultEdit ? 'edited' : ''}`}
                                                                        onClick={() => setEditingCell({ assegnazioneKey, type: 'default', currentValue: operatoriRichiestiDefault })}
                                                                        title="Click per modificare"
                                                                    >
                                                                        {isEditingDefault ? (
                                                                            <input
                                                                                type="number"
                                                                                className="cell-edit-input"
                                                                                defaultValue={operatoriRichiestiDefault}
                                                                                min="1"
                                                                                max={totaleOp}
                                                                                autoFocus
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                onBlur={(e) => {
                                                                                    const newValue = parseInt(e.target.value, 10);
                                                                                    if (newValue >= 1 && newValue <= totaleOp) {
                                                                                        updateOperatoriRichiestiDefault(assegnazioneKey, newValue);
                                                                                    }
                                                                                    setEditingCell(null);
                                                                                }}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === 'Enter') {
                                                                                        const newValue = parseInt(e.target.value, 10);
                                                                                        if (newValue >= 1 && newValue <= totaleOp) {
                                                                                            updateOperatoriRichiestiDefault(assegnazioneKey, newValue);
                                                                                        }
                                                                                        setEditingCell(null);
                                                                                    } else if (e.key === 'Escape') {
                                                                                        setEditingCell(null);
                                                                                    }
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <span className="counter-value">
                                                                                {operatoriRichiestiDefault}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="calendar-operatori">
                                                                        <span className="counter-value">{totaleOp}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="assegnazioni-right-cell">
                                                                    <div className="weeks-grid-by-month">
                                                                        {weeksByFiscalMonth.map((weeks, fmIdx) => (
                                                                            <div key={fmIdx} className="month-weeks-block">
                                                                                {weeks.map(week => {
                                                                                    const disponibili = getOperatoriDisponibili(assegnazioneGroup.dipendenti, week.weekNum, week.anno);
                                                                                    const totale = assegnazioneGroup.dipendenti.length;
                                                                                    const inFerie = totale - disponibili;
                                                                                    const minimoRichiesto = getOperatoriRichiestiSettimana(assegnazioneKey, week.weekNum, week.anno, totale);
                                                                                    const isWeekEdited = isSettimanaEdited(assegnazioneKey, week.weekNum, week.anno);
                                                                                    const isEditing = editingCell?.assegnazioneKey === assegnazioneKey &&
                                                                                                     editingCell?.weekNum === week.weekNum &&
                                                                                                     editingCell?.anno === week.anno &&
                                                                                                     editingCell?.type === 'settimana';
                                                                                    const isInSelection = isCellInSelection(assegnazioneKey, week.weekNum, week.anno, weeksStructure);

                                                                                    let cellClass = 'week-cell-assegnazione';
                                                                                    if (inFerie < minimoRichiesto) cellClass += ' below-limit';
                                                                                    else if (inFerie === minimoRichiesto) cellClass += ' at-limit';
                                                                                    else cellClass += ' over-limit';
                                                                                    if (isWeekEdited) cellClass += ' edited';
                                                                                    if (isInSelection) cellClass += ' selected';

                                                                                    return (
                                                                                        <div
                                                                                            key={`${week.weekNum}-${week.anno}`}
                                                                                            className={cellClass}
                                                                                            title={`S${week.weekNum}: ${inFerie} richieste / ${minimoRichiesto} max consentite${isWeekEdited ? ' (modificato)' : ''} - Trascina per selezione multipla`}
                                                                                            onContextMenu={(e) => {
                                                                                                if (isWeekEdited) {
                                                                                                    e.preventDefault();
                                                                                                    setContextMenu({ x: e.clientX, y: e.clientY, assegnazioneKey, weekNum: week.weekNum, anno: week.anno });
                                                                                                }
                                                                                            }}
                                                                                            onMouseDown={(e) => handleDragStart(assegnazioneKey, week.weekNum, week.anno, e)}
                                                                                            onMouseEnter={() => handleDragMove(assegnazioneKey, week.weekNum, week.anno)}
                                                                                            onMouseUp={(e) => handleDragEnd(e, weeksStructure)}
                                                                                            onClick={(e) => {
                                                                                                if (!dragSelection || (dragSelection.startWeek === week.weekNum && dragSelection.startAnno === week.anno &&
                                                                                                    dragSelection.endWeek === week.weekNum && dragSelection.endAnno === week.anno)) {
                                                                                                    setEditingCell({ assegnazioneKey, weekNum: week.weekNum, anno: week.anno, currentValue: minimoRichiesto, type: 'settimana' });
                                                                                                    setDragSelection(null);
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            {isEditing ? (
                                                                                                <input
                                                                                                    type="number"
                                                                                                    className="cell-edit-input"
                                                                                                    defaultValue={minimoRichiesto}
                                                                                                    min="1"
                                                                                                    max={totale}
                                                                                                    autoFocus
                                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                                                    onBlur={(e) => {
                                                                                                        const newValue = parseInt(e.target.value, 10);
                                                                                                        if (newValue >= 1 && newValue <= totale && newValue !== minimoRichiesto) {
                                                                                                            updateOperatoriRichiestiSettimana(assegnazioneKey, week.weekNum, week.anno, newValue);
                                                                                                        }
                                                                                                        setEditingCell(null);
                                                                                                    }}
                                                                                                    onKeyDown={(e) => {
                                                                                                        if (e.key === 'Enter') {
                                                                                                            const newValue = parseInt(e.target.value, 10);
                                                                                                            if (newValue >= 1 && newValue <= totale && newValue !== minimoRichiesto) {
                                                                                                                updateOperatoriRichiestiSettimana(assegnazioneKey, week.weekNum, week.anno, newValue);
                                                                                                            }
                                                                                                            setEditingCell(null);
                                                                                                        } else if (e.key === 'Escape') {
                                                                                                            setEditingCell(null);
                                                                                                        }
                                                                                                    }}
                                                                                                />
                                                                                            ) : (
                                                                                                <span className="cell-fraction">
                                                                                                    <span className="numerator">{inFerie}</span>
                                                                                                    <span className="separator">/</span>
                                                                                                    <span className="denominator">{minimoRichiesto}{isWeekEdited && <span className="edited-indicator">*</span>}</span>
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Sub-header dipendenti (se espanso) */}
                                                            {isAssegnazioneExpanded && (
                                                                <div className="assegnazioni-row dip-columns-header">
                                                                    <div className="assegnazioni-left-cell sticky-left dip-header-bg">
                                                                        <div className="detail-spacer"></div>
                                                                        <div className="dip-col-name-header">Dipendente</div>
                                                                        <div className="dip-col-counter-header">Rich.</div>
                                                                        <div className="dip-col-counter-header">Conf.</div>
                                                                    </div>
                                                                    <div className="assegnazioni-right-cell dip-header-bg"></div>
                                                                </div>
                                                            )}

                                                            {/* Dipendenti rows (se espanso) */}
                                                            {isAssegnazioneExpanded && assegnazioneGroup.dipendenti.map(dip => {
                                                                const dipStats = getStats(dip.id, selectedYear);
                                                                return (
                                                                    <div key={dip.id} className={`assegnazioni-row dipendente-row${dipStats.superaLimite ? ' alert-row' : ''}`}>
                                                                        <div className={`assegnazioni-left-cell sticky-left${dipStats.superaLimite ? ' dipendente-alert-bg' : ' dipendente-bg'}`}>
                                                                            <div className="detail-spacer"></div>
                                                                            <div className="dip-col-name">
                                                                                <button
                                                                                    className="btn-nascondi-dip"
                                                                                    title="Nascondi dipendente"
                                                                                    onClick={(e) => { e.stopPropagation(); handleHideRow(dip.id); }}
                                                                                >
                                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"></path>
                                                                                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"></path>
                                                                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                                                                    </svg>
                                                                                </button>
                                                                                <button
                                                                                    className="btn-pending-dip"
                                                                                    title="Gestione richieste dipendente"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setShowDipendentePendingModal({
                                                                                            dipendenteId: dip.id,
                                                                                            dipendenteName: `${dip.cognome} ${dip.nome}`
                                                                                        });
                                                                                    }}
                                                                                >
                                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                                                        <polyline points="14 2 14 8 20 8"></polyline>
                                                                                        <line x1="16" y1="13" x2="8" y2="13"></line>
                                                                                        <line x1="16" y1="17" x2="8" y2="17"></line>
                                                                                    </svg>
                                                                                </button>
                                                                                <span className="dipendente-nome-small">
                                                                                    {dip.cognome} {dip.nome}
                                                                                </span>
                                                                                {dip.assegnazione_dett && (
                                                                                    <span className="badge-assegnazione-dett">{dip.assegnazione_dett}</span>
                                                                                )}
                                                                                {dipStats.superaLimite && (
                                                                                    <span
                                                                                        className="alert-badge"
                                                                                        onMouseEnter={(e) => {
                                                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                                                            setAlertTooltip({ x: rect.left + rect.width / 2, y: rect.top, errors: getAlertErrors(dipStats) });
                                                                                        }}
                                                                                        onMouseLeave={() => setAlertTooltip(null)}
                                                                                    >!</span>
                                                                                )}
                                                                            </div>
                                                                            <div className="dip-col-counter">
                                                                                <span className={`dip-badge ${dipStats.giorniRichiesti >= GIORNI_FERIE_ANNUALI ? 'badge-green' : 'badge-red'}`}>
                                                                                    {dipStats.giorniRichiesti}/{GIORNI_FERIE_ANNUALI}
                                                                                </span>
                                                                            </div>
                                                                            <div className="dip-col-counter">
                                                                                <span className={`dip-badge ${dipStats.giorniConfermati >= GIORNI_FERIE_ANNUALI ? 'badge-green' : 'badge-red'}`}>
                                                                                    {dipStats.giorniConfermati}/{GIORNI_FERIE_ANNUALI}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="assegnazioni-right-cell">
                                                                            {renderWeekView(dip)}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                  </div>
                                </div>
                            </div>

                            {/* Empty state */}
                            {filteredCommesseForAssegnazioneNew.length === 0 && (
                                <div className="empty-state">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="9" cy="7" r="4"></circle>
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                    </svg>
                                    <h3>Nessuna commessa trovata</h3>
                                    <p>Non ci sono dati da visualizzare</p>
                                </div>
                            )}

                            {/* Context menu ripristina default */}
                            {contextMenu && (
                                <div className="context-menu-overlay" onClick={() => { setContextMenu(null); setDragSelection(null); }}>
                                    <div
                                        className="context-menu"
                                        style={{
                                            left: Math.min(contextMenu.x, window.innerWidth - 200),
                                            top: Math.min(contextMenu.y, window.innerHeight - 80)
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="context-menu-header">
                                            <span>Settimana {contextMenu.weekNum}</span>
                                        </div>
                                        <button
                                            className="context-menu-item"
                                            onClick={() => {
                                                resetOperatoriRichiestiSettimana(contextMenu.assegnazioneKey, contextMenu.weekNum, contextMenu.anno);
                                                setContextMenu(null);
                                            }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="1 4 1 10 7 10"></polyline>
                                                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                                            </svg>
                                            Ripristina default
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Popup editing massivo */}
                            {showBulkEditPopup && (
                                <div className="bulk-edit-overlay" onClick={cancelSelection}>
                                    <div
                                        className="bulk-edit-popup"
                                        style={{
                                            left: Math.min(showBulkEditPopup.position.x, window.innerWidth - 250),
                                            top: Math.min(showBulkEditPopup.position.y, window.innerHeight - 150)
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="bulk-edit-header">
                                            <span className="bulk-edit-title">
                                                Modifica {showBulkEditPopup.selectedWeeks.length} settimane
                                            </span>
                                            <button className="bulk-edit-close" onClick={cancelSelection}>×</button>
                                        </div>
                                        <div className="bulk-edit-body">
                                            <label>Nuovo valore max richieste:</label>
                                            <input
                                                type="number"
                                                className="bulk-edit-input"
                                                min="1"
                                                defaultValue={1}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        const value = parseInt(e.target.value, 10);
                                                        if (value >= 1) applyBulkValue(value);
                                                    } else if (e.key === 'Escape') {
                                                        cancelSelection();
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="bulk-edit-footer">
                                            <button className="bulk-edit-cancel" onClick={cancelSelection}>Annulla</button>
                                            <button
                                                className="bulk-edit-apply"
                                                onClick={(e) => {
                                                    const input = e.target.closest('.bulk-edit-popup').querySelector('.bulk-edit-input');
                                                    const value = parseInt(input.value, 10);
                                                    if (value >= 1) applyBulkValue(value);
                                                }}
                                            >
                                                Applica
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}
                    </div>
                )}

                {/* Tab Piano Ferie Quotidiano */}
                {selectedTab === 'Piano Ferie Quotidiano' && (
                    <div className="tab-content-wrapper">
                        <div className="piano-ferie-quotidiano-container">
                            <p className="piano-ferie-quotidiano-placeholder">
                                Sezione Piano Ferie Quotidiano in fase di sviluppo.
                            </p>
                        </div>
                    </div>
                )}

            </div>

            {/* Modal Spostamento - Root level per disponibilità da tutte le tab */}
            {showMoveDialog && (() => {
                const weeksByFiscalMonth = Array(12).fill(null).map(() => []);
                weeksStructure.forEach(week => {
                    const fmIdx = week.fiscalMonthIndex;
                    if (fmIdx !== undefined && fmIdx >= 0 && fmIdx < 12) {
                        weeksByFiscalMonth[fmIdx].push(week);
                    }
                });

                return (
                    <div className="modal-overlay" onClick={() => setShowMoveDialog(null)}>
                        <div className="modal-container modal-move" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Sposta richiesta ferie</h3>
                                <button className="modal-close" onClick={() => setShowMoveDialog(null)}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>Seleziona la nuova settimana per spostare la richiesta dalla settimana {showMoveDialog.weekNum}:</p>
                                <div className="week-select-by-month">
                                    {weeksByFiscalMonth.map((weeks, fmIdx) => (
                                        <div key={fmIdx} className="month-week-group">
                                            <div className="month-week-label">{mesiFiscali[fmIdx]}</div>
                                            <div className="month-week-buttons">
                                                {weeks.map(week => {
                                                    const ferie = ferieData[showMoveDialog.dipendenteId]?.[week.anno] || [];
                                                    const weekFerie = ferie.find(f => f.weekNum === week.weekNum);
                                                    const stato = weekFerie?.stato || null;
                                                    const isDisabled = !!stato || (week.weekNum === showMoveDialog.weekNum && week.anno === showMoveDialog.anno);
                                                    const startDate = formatDateShort(week.startDate);
                                                    const endDate = formatDateShort(week.endDate);
                                                    return (
                                                        <button
                                                            key={`${week.weekNum}-${week.anno}`}
                                                            className={`week-select-btn ${isDisabled ? 'disabled' : ''} ${week.weekNum === showMoveDialog.weekNum ? 'current' : ''}`}
                                                            disabled={isDisabled}
                                                            onClick={() => handleMoveFerie(showMoveDialog.dipendenteId, showMoveDialog.weekNum, week.weekNum, showMoveDialog.sourceStato, showMoveDialog.originalWeekFrom)}
                                                            title={`S${week.weekNum} ${week.anno}: ${startDate} - ${endDate}`}
                                                        >
                                                            <span className="week-num">S{week.weekNum}</span>
                                                            <span className="week-dates">{startDate}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Modal Recap Ferie - Root level per disponibilità da tutte le tab */}
            {showRecapModal && (
                <div className="modal-overlay" onClick={() => setShowRecapModal(null)}>
                    <div className="modal-container modal-recap" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Dettaglio Ferie</h3>
                            <button className="modal-close" onClick={() => setShowRecapModal(null)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body recap-body">
                            <div className="recap-dipendente">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                                <span>{showRecapModal.dipendenteName}</span>
                            </div>
                            <div className="recap-info-grid">
                                <div className="recap-info-item">
                                    <span className="recap-label">Stato</span>
                                    <span
                                        className="recap-value stato-badge"
                                        style={{ backgroundColor: STATI_FERIE[showRecapModal.stato]?.color || '#94A3B8' }}
                                    >
                                        {STATI_FERIE[showRecapModal.stato]?.label || showRecapModal.stato}
                                    </span>
                                </div>
                                <div className="recap-info-item">
                                    <span className="recap-label">Settimana</span>
                                    <span className="recap-value">S{showRecapModal.weekNum}</span>
                                </div>
                                <div className="recap-info-item">
                                    <span className="recap-label">Data Inizio</span>
                                    <span className="recap-value">
                                        {showRecapModal.startDate?.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                                    </span>
                                </div>
                                <div className="recap-info-item">
                                    <span className="recap-label">Data Fine</span>
                                    <span className="recap-value">
                                        {showRecapModal.endDate?.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                                    </span>
                                </div>
                            </div>

                            {(showRecapModal.stato === 'spostata' || showRecapModal.stato === 'spostata_accordo') && showRecapModal.settimanaOriginale && (
                                <div className="recap-spostamento-details" style={{ marginTop: '16px', padding: '12px', background: '#F0F9FF', borderRadius: '8px', border: '1px solid #BAE6FD' }}>
                                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#0369A1', marginBottom: '8px', textTransform: 'uppercase' }}>
                                        Dettaglio Spostamento
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#0F172A' }}>
                                        <span style={{ padding: '4px 10px', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontWeight: '600' }}>
                                            {showRecapModal.settimanaOriginale}
                                        </span>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                                            <path d="M5 12h14"></path>
                                            <path d="m12 5 7 7-7 7"></path>
                                        </svg>
                                        <span style={{ padding: '4px 10px', background: showRecapModal.stato === 'spostata_accordo' ? '#DBEAFE' : '#E0F2FE', color: showRecapModal.stato === 'spostata_accordo' ? '#2563EB' : '#0369A1', borderRadius: '6px', fontWeight: '600' }}>
                                            S{showRecapModal.weekNum} (Nuova)
                                        </span>
                                    </div>
                                </div>
                            )}

                            {(showRecapModal.stato === 'proposta_rifiutata' || showRecapModal.stato === 'proposta_rifiutata_accordo') && showRecapModal.settimanaDestinazione && (
                                <div className="recap-spostamento-details" style={{ marginTop: '16px', padding: '12px', background: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA' }}>
                                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#DC2626', marginBottom: '8px', textTransform: 'uppercase' }}>
                                        Dettaglio Spostamento
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#0F172A' }}>
                                        <span style={{ padding: '4px 10px', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontWeight: '600' }}>
                                            S{showRecapModal.weekNum} (Origine)
                                        </span>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                                            <path d="M5 12h14"></path>
                                            <path d="m12 5 7 7-7 7"></path>
                                        </svg>
                                        <span style={{ padding: '4px 10px', background: showRecapModal.stato === 'proposta_rifiutata_accordo' ? '#DBEAFE' : '#E0F2FE', color: showRecapModal.stato === 'proposta_rifiutata_accordo' ? '#2563EB' : '#0369A1', borderRadius: '6px', fontWeight: '600' }}>
                                            {showRecapModal.settimanaDestinazione}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {showRecapModal.stato === 'nuova_richiesta' && (
                                <div className="recap-actions">
                                    <button
                                        className="btn-conferma-ferie"
                                        onClick={() => handleTrasmettiConferma(showRecapModal.dipendenteId, showRecapModal.weekNum, showRecapModal.anno)}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                        </svg>
                                        Trasmetti conferma Interacta
                                    </button>
                                    <button
                                        className="btn-modifica-ferie"
                                        onClick={() => {
                                            setShowRecapModal(null);
                                            handleOpenMoveDialog(showRecapModal.dipendenteId, showRecapModal.weekNum, 'nuova_richiesta', undefined);
                                        }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                        </svg>
                                        Modifica / Sposta settimana
                                    </button>
                                </div>
                            )}
                            {showRecapModal.stato === 'bozza' && (
                                <div className="recap-actions">
                                    <button
                                        className="btn-trasmetti-spostamento"
                                        style={{ marginBottom: '10px' }}
                                        onClick={() => handleTrasmettiBozza(showRecapModal.dipendenteId, showRecapModal.weekNum, showRecapModal.anno)}
                                        disabled={trasmettiBozzaInCorso}
                                    >
                                        {trasmettiBozzaInCorso ? (
                                            <div className="btn-spinner-small" />
                                        ) : (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                            </svg>
                                        )}
                                        {trasmettiBozzaInCorso ? 'Trasmissione...' : 'Trasmetti a Interacta'}
                                    </button>
                                    <button
                                        className="btn-elimina-bozza"
                                        onClick={() => handleDeleteBozza(showRecapModal.dipendenteId, showRecapModal.weekNum, showRecapModal.anno)}
                                        disabled={trasmettiBozzaInCorso}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            <line x1="10" y1="11" x2="10" y2="17"></line>
                                            <line x1="14" y1="11" x2="14" y2="17"></line>
                                        </svg>
                                        Elimina bozza
                                    </button>
                                </div>
                            )}
                            {showRecapModal.stato === 'spostata_bozza' && (() => {
                                const spostamento = spostamentiBozza[`${showRecapModal.dipendenteId}-${showRecapModal.weekNum}-${showRecapModal.anno}`];
                                const weekFrom = spostamento?.weekFrom;
                                const weekFromData = weeksStructure.find(w => w.weekNum === weekFrom);
                                const weekToData = weeksStructure.find(w => w.weekNum === showRecapModal.weekNum);
                                return (
                                    <div className="recap-actions">
                                        <div style={{ marginBottom: '12px', padding: '12px', background: '#F0F9FF', borderRadius: '8px', border: '1px solid #BAE6FD' }}>
                                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#0369A1', marginBottom: '8px', textTransform: 'uppercase' }}>
                                                Dettaglio Spostamento (Bozza)
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#0F172A' }}>
                                                <span style={{ padding: '4px 10px', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontWeight: '600' }}>
                                                    S{weekFrom} {weekFromData ? `${weekFromData.startDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} - ${weekFromData.endDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}` : ''}
                                                </span>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                                                    <path d="M5 12h14"></path>
                                                    <path d="m12 5 7 7-7 7"></path>
                                                </svg>
                                                <span style={{ padding: '4px 10px', background: '#E0F2FE', color: '#0369A1', borderRadius: '6px', fontWeight: '600' }}>
                                                    S{showRecapModal.weekNum} {weekToData ? `${weekToData.startDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} - ${weekToData.endDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}` : ''}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            className="btn-modifica-ferie"
                                            onClick={() => {
                                                setShowRecapModal(null);
                                                handleOpenMoveDialog(showRecapModal.dipendenteId, showRecapModal.weekNum, 'spostata_bozza', weekFrom);
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                            Sposta in altra settimana
                                        </button>
                                        <button
                                            className="btn-annulla-spostamento"
                                            onClick={() => handleAnnullaSpostamentoBozza(showRecapModal.dipendenteId, weekFrom, showRecapModal.weekNum)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                                <path d="M3 3v5h5"></path>
                                            </svg>
                                            Annulla spostamento
                                        </button>
                                        <button
                                            className="btn-trasmetti-spostamento"
                                            onClick={() => handleTrasmettiSpostamento(showRecapModal.dipendenteId, showRecapModal.weekNum)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                            </svg>
                                            Trasmetti a Interacta
                                        </button>
                                    </div>
                                );
                            })()}
                            {showRecapModal.stato === 'rifiutata_bozza' && (() => {
                                const destinazione = Object.entries(spostamentiBozza).find(([key, value]) =>
                                    key.startsWith(`${showRecapModal.dipendenteId}-`) &&
                                    key.endsWith(`-${showRecapModal.anno}`) &&
                                    value.weekFrom === showRecapModal.weekNum
                                );
                                const weekTo = destinazione ? destinazione[1].weekTo : null;
                                const weekFrom = showRecapModal.weekNum;
                                const weekFromData = weeksStructure.find(w => w.weekNum === weekFrom);
                                const weekToData = weeksStructure.find(w => w.weekNum === weekTo);
                                return (
                                    <div className="recap-actions">
                                        <div style={{ marginBottom: '12px', padding: '12px', background: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA' }}>
                                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#DC2626', marginBottom: '8px', textTransform: 'uppercase' }}>
                                                Dettaglio Spostamento (Bozza)
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#0F172A' }}>
                                                <span style={{ padding: '4px 10px', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontWeight: '600' }}>
                                                    S{weekFrom} {weekFromData ? `${weekFromData.startDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} - ${weekFromData.endDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}` : ''}
                                                </span>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                                                    <path d="M5 12h14"></path>
                                                    <path d="m12 5 7 7-7 7"></path>
                                                </svg>
                                                <span style={{ padding: '4px 10px', background: '#E0F2FE', color: '#0369A1', borderRadius: '6px', fontWeight: '600' }}>
                                                    S{weekTo} {weekToData ? `${weekToData.startDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} - ${weekToData.endDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}` : ''}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            className="btn-annulla-spostamento"
                                            onClick={() => handleAnnullaSpostamentoBozza(showRecapModal.dipendenteId, weekFrom, weekTo)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                                <path d="M3 3v5h5"></path>
                                            </svg>
                                            Annulla spostamento
                                        </button>
                                        <p className="recap-note">
                                            La trasmissione avverrà dalla settimana destinazione
                                        </p>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Modale Riepilogo Spostamenti */}
            {showRiepilogoSpostamenti && (() => {
                // Arricchisci, filtra e ordina gli spostamenti
                const spostamentiArricchiti = Object.entries(spostamentiBozza).map(([key, value]) => {
                    const parts = key.split('-');
                    const dipId = parseInt(parts[0]);
                    const weekTo = parseInt(parts[1]);
                    const dip = dipendenti.find(d => d.id === dipId);
                    const weekFromData = weeksStructure.find(w => w.weekNum === value.weekFrom);
                    const weekToData = weeksStructure.find(w => w.weekNum === weekTo);
                    return { key, value, dipId, weekTo, dip, weekFromData, weekToData };
                });

                // Opzioni filtri derivate dai dipendenti presenti negli spostamenti
                const riepilogoCantieri = [...new Set(spostamentiArricchiti.map(s => s.dip?.cantiere).filter(Boolean))].sort((a, b) => a.localeCompare(b));
                const riepilogoCommesse = [...new Set(spostamentiArricchiti.map(s => s.dip?.commessa).filter(Boolean))].sort((a, b) => a.localeCompare(b));
                const riepilogoAssegnazioni = [...new Set(spostamentiArricchiti.map(s => s.dip?.assegnazione_dett).filter(Boolean))].sort((a, b) => a.localeCompare(b));

                // Filtra
                const searchLower = searchRiepilogo.toLowerCase();
                const spostamentiFiltrati = spostamentiArricchiti.filter(s => {
                    if (!s.dip) return false;
                    const matchCantiere = filtroCantiereRiepilogo.length === 0 || filtroCantiereRiepilogo.some(f => f.value === s.dip.cantiere);
                    const matchCommessa = filtroCommessaRiepilogo.length === 0 || filtroCommessaRiepilogo.some(f => f.value === s.dip.commessa);
                    const matchAssegnazione = filtroAssegnazioneRiepilogo.length === 0 || filtroAssegnazioneRiepilogo.some(f => f.value === s.dip.assegnazione_dett);
                    const matchSearch = !searchRiepilogo || `${s.dip.cognome} ${s.dip.nome}`.toLowerCase().includes(searchLower);
                    return matchCantiere && matchCommessa && matchAssegnazione && matchSearch;
                });

                // Ordina A-Z per cognome
                spostamentiFiltrati.sort((a, b) => {
                    const cognomeA = a.dip?.cognome || '';
                    const cognomeB = b.dip?.cognome || '';
                    return cognomeA.localeCompare(cognomeB);
                });

                const formatWeekRange = (weekData) => {
                    if (!weekData) return '';
                    const start = weekData.startDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    const end = weekData.endDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    return `${start} - ${end}`;
                };

                return (
                    <div className="modal-overlay" onClick={() => setShowRiepilogoSpostamenti(false)}>
                        <div className="modal-container modal-riepilogo-spostamenti" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)' }}>
                                <h3>Riepilogo Spostamenti da Trasmettere</h3>
                                <button className="modal-close" onClick={() => setShowRiepilogoSpostamenti(false)}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                            <div className="modal-body" style={{ padding: '20px' }}>
                                {Object.keys(spostamentiBozza).length === 0 ? (
                                    <p style={{ textAlign: 'center', color: '#64748B' }}>Nessuno spostamento in bozza.</p>
                                ) : (
                                    <>
                                        {/* Filtri */}
                                        <div className="riepilogo-filters">
                                            <div className="filter-item">
                                                <label>Cantiere</label>
                                                <Select
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    classNamePrefix="piano-ferie-select"
                                                    value={filtroCantiereRiepilogo}
                                                    options={riepilogoCantieri.map(c => ({ value: c, label: c }))}
                                                    onChange={(selected) => setFiltroCantiereRiepilogo(selected || [])}
                                                    isSearchable={true}
                                                    placeholder="Tutti i cantieri"
                                                />
                                            </div>
                                            <div className="filter-item">
                                                <label>Commessa</label>
                                                <Select
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    classNamePrefix="piano-ferie-select"
                                                    value={filtroCommessaRiepilogo}
                                                    options={riepilogoCommesse.map(c => ({ value: c, label: c }))}
                                                    onChange={(selected) => setFiltroCommessaRiepilogo(selected || [])}
                                                    isSearchable={true}
                                                    placeholder="Tutte le commesse"
                                                />
                                            </div>
                                            <div className="filter-item">
                                                <label>Assegnazione</label>
                                                <Select
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    classNamePrefix="piano-ferie-select"
                                                    value={filtroAssegnazioneRiepilogo}
                                                    options={riepilogoAssegnazioni.map(a => ({ value: a, label: a }))}
                                                    onChange={(selected) => setFiltroAssegnazioneRiepilogo(selected || [])}
                                                    isSearchable={true}
                                                    placeholder="Tutte le assegnazioni"
                                                />
                                            </div>
                                            <div className="filter-item">
                                                <label>Cerca dipendente</label>
                                                <input
                                                    type="text"
                                                    value={searchRiepilogo}
                                                    onChange={(e) => setSearchRiepilogo(e.target.value)}
                                                    placeholder="Cerca per nome..."
                                                />
                                            </div>
                                        </div>

                                        {/* Tabella */}
                                        <div className="riepilogo-spostamenti-table-wrapper">
                                            <table className="riepilogo-spostamenti-table">
                                                <thead>
                                                    <tr>
                                                        <th>Dipendente</th>
                                                        <th>Telefono</th>
                                                        <th>Contratto</th>
                                                        <th>Assegnazione</th>
                                                        <th>Dal</th>
                                                        <th>Al</th>
                                                        <th>Azioni</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {spostamentiFiltrati.map(({ key, value, dipId, weekTo, dip, weekFromData, weekToData }) => {
                                                        const isTrasmitting = trasmettiInCorso[key];
                                                        return (
                                                            <tr key={key}>
                                                                <td style={{ fontWeight: '600', color: '#0F172A', whiteSpace: 'nowrap' }}>
                                                                    {dip ? `${dip.cognome} ${dip.nome}` : `ID ${dipId}`}
                                                                </td>
                                                                <td style={{ fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>
                                                                    {dip?.cellulare || dip?.telFisso ? (
                                                                        <>
                                                                            {dip.cellulare && <span>{dip.cellulare}</span>}
                                                                            {dip.cellulare && dip.telFisso && <br />}
                                                                            {dip.telFisso && <span>{dip.telFisso}</span>}
                                                                        </>
                                                                    ) : '—'}
                                                                </td>
                                                                <td style={{ fontSize: '12px', color: '#475569' }}>
                                                                    {dip?.contratto || '—'}
                                                                </td>
                                                                <td>
                                                                    {dip?.assegnazione_dett ? (
                                                                        <span className="badge-assegnazione">{dip.assegnazione_dett}</span>
                                                                    ) : '—'}
                                                                </td>
                                                                <td>
                                                                    <span style={{ padding: '3px 8px', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                                                        S{value.weekFrom} {formatWeekRange(weekFromData)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span style={{ padding: '3px 8px', background: '#E0F2FE', color: '#0369A1', borderRadius: '6px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                                                        S{weekTo} {formatWeekRange(weekToData)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                                        <button
                                                                            className="btn-riepilogo-elimina"
                                                                            title="Rimuovi"
                                                                            disabled={isTrasmitting}
                                                                            onClick={() => handleAnnullaSpostamentoBozza(dipId, value.weekFrom, weekTo)}
                                                                        >
                                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                                            </svg>
                                                                        </button>
                                                                        <button
                                                                            className="btn-riepilogo-trasmetti"
                                                                            title="Trasmetti"
                                                                            disabled={isTrasmitting}
                                                                            onClick={async () => {
                                                                                setTrasmettiInCorso(prev => ({ ...prev, [key]: true }));
                                                                                await handleTrasmettiSpostamento(dipId, weekTo);
                                                                                setTrasmettiInCorso(prev => { const n = { ...prev }; delete n[key]; return n; });
                                                                            }}
                                                                        >
                                                                            {isTrasmitting ? (
                                                                                <div className="btn-spinner-small" />
                                                                            ) : (
                                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                    <line x1="22" y1="2" x2="11" y2="13"></line>
                                                                                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                                                                </svg>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                            {Object.keys(spostamentiBozza).length > 0 && (
                                <div style={{ padding: '16px 20px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                    <button
                                        className="btn-export-excel"
                                        onClick={handleExportExcelRiepilogo}
                                        title="Esporta in Excel"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="7 10 12 15 17 10"></polyline>
                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                        </svg>
                                        Esporta Excel
                                    </button>
                                    <button
                                        className="btn-riepilogo-chiudi"
                                        onClick={() => setShowRiepilogoSpostamenti(false)}
                                    >
                                        Chiudi
                                    </button>
                                    <button
                                        className="btn-riepilogo-trasmetti-tutte"
                                        onClick={async () => {
                                            await handleTrasmettiTutti();
                                            setShowRiepilogoSpostamenti(false);
                                        }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="22" y1="2" x2="11" y2="13"></line>
                                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                        </svg>
                                        Trasmetti tutte ({spostamentiFiltrati.length})
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* Modale Gestione Richieste Dipendente */}
            {showDipendentePendingModal && (() => {
                const dipId = showDipendentePendingModal.dipendenteId;
                const dipName = showDipendentePendingModal.dipendenteName;

                // Sezione 2: Spostamenti in bozza per questo dipendente (calcolati prima per filtrare le nuove richieste)
                const spostamentiDip = Object.entries(spostamentiBozza)
                    .filter(([key]) => key.startsWith(`${dipId}-`))
                    .map(([key, value]) => {
                        const parts = key.split('-');
                        const weekTo = parseInt(parts[1]);
                        const annoTo = parseInt(parts[2]);
                        const weekFromData = weeksStructure.find(w => w.weekNum === value.weekFrom);
                        const weekToData = weeksStructure.find(w => w.weekNum === weekTo && w.anno === annoTo);
                        return { key, weekTo, annoTo, weekFrom: value.weekFrom, weekFromData, weekToData, postId: value.postId };
                    });

                // Settimane origine degli spostamenti in bozza (da escludere dalle nuove richieste)
                const settimaneConSpostamento = new Set(spostamentiDip.map(s => s.weekFrom));

                // Sezione 1: Nuove richieste (stato === 'nuova_richiesta')
                // Escludi quelle gia' coperte da uno spostamento in bozza
                // Tagga ogni ferie con l'anno corretto prima di unire
                const ferieAnnoInizio = (ferieData[dipId]?.[selectedYear] || []).map(f => ({ ...f, _anno: selectedYear }));
                const ferieAnnoFine = (ferieData[dipId]?.[selectedYear + 1] || []).map(f => ({ ...f, _anno: selectedYear + 1 }));
                const allFerie = [...ferieAnnoInizio, ...ferieAnnoFine];
                // Deduplica per weekNum-anno, escludi settimane con spostamento bozza attivo
                const nuoveRichiesteMap = {};
                allFerie.filter(f => f.stato === 'nuova_richiesta' && !settimaneConSpostamento.has(f.weekNum)).forEach(f => {
                    const key = `${f.weekNum}-${f._anno}`;
                    if (!nuoveRichiesteMap[key]) {
                        nuoveRichiesteMap[key] = f;
                    }
                });
                const nuoveRichieste = Object.values(nuoveRichiesteMap);

                // Sezione 3: Bozze create dall'operatore
                const bozzeDip = Object.keys(bozzeData)
                    .filter(key => key.startsWith(`${dipId}-`))
                    .map(key => {
                        const parts = key.split('-');
                        const weekNum = parseInt(parts[1]);
                        const anno = parseInt(parts[2]);
                        const weekData = weeksStructure.find(w => w.weekNum === weekNum && w.anno === anno);
                        return { key, weekNum, anno, weekData };
                    })
                    .filter(b => b.weekData);

                const totalePending = nuoveRichieste.length + spostamentiDip.length + bozzeDip.length;

                const formatWeekRange = (weekData) => {
                    if (!weekData) return '';
                    const start = weekData.startDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    const end = weekData.endDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    return `${start} - ${end}`;
                };

                return (
                    <div className="modal-overlay" onClick={() => setShowDipendentePendingModal(null)}>
                        <div className="modal-container modal-dipendente-pending" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
                            {trasmettiPendingInCorso['_trasmetti_tutte'] && (
                                <div className="pf-loader-overlay">
                                    <Lf23Loader testo="Trasmissione in corso..." />
                                </div>
                            )}
                            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)' }}>
                                <h3>Gestione Richieste - {dipName}</h3>
                                <button className="modal-close" onClick={() => setShowDipendentePendingModal(null)}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                            <div className="modal-body" style={{ padding: '20px', maxHeight: '65vh', overflowY: 'auto' }}>

                                {/* Sezione 1: Nuove Richieste */}
                                <div className="pending-section">
                                    <div className="pending-section-header">
                                        <span className="pending-section-icon" style={{ background: '#FEF3C7' }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                            </svg>
                                        </span>
                                        <span className="pending-section-title">Nuove Richieste</span>
                                        <span className="pending-section-count" style={{ background: '#FEF3C7', color: '#92400E' }}>{nuoveRichieste.length}</span>
                                    </div>
                                    {nuoveRichieste.length === 0 ? (
                                        <p className="pending-empty">Nessuna nuova richiesta in attesa.</p>
                                    ) : (
                                        <div className="pending-list">
                                            {nuoveRichieste.map(f => {
                                                const weekData = weeksStructure.find(w => w.weekNum === f.weekNum && w.anno === f._anno);
                                                const pKey = `conferma-${dipId}-${f.weekNum}-${f._anno}`;
                                                const isTrasmitting = trasmettiPendingInCorso[pKey];
                                                return (
                                                    <div key={pKey} className="pending-item">
                                                        <div className="pending-item-info">
                                                            <span className="pending-week-badge" style={{ background: '#FEF3C7', color: '#92400E' }}>
                                                                S{f.weekNum}
                                                            </span>
                                                            <span className="pending-item-dates">
                                                                {weekData ? formatWeekRange(weekData) : ''}
                                                            </span>
                                                            <span className="stato-badge" style={{ backgroundColor: STATI_FERIE.nuova_richiesta.color, fontSize: '11px', padding: '2px 8px' }}>
                                                                {STATI_FERIE.nuova_richiesta.label}
                                                            </span>
                                                        </div>
                                                        <button
                                                            className="btn-pending-trasmetti"
                                                            disabled={isTrasmitting}
                                                            onClick={async () => {
                                                                if (!window.confirm(`Sei sicuro di voler trasmettere la conferma per la settimana S${f.weekNum}?`)) return;
                                                                setTrasmettiPendingInCorso(prev => ({ ...prev, [pKey]: true }));
                                                                const annoEff = f._anno;
                                                                const ferie = ferieData[dipId]?.[annoEff] || [];
                                                                const ferieWeek = ferie.find(fw => fw.weekNum === f.weekNum && fw.stato === 'nuova_richiesta');
                                                                if (!ferieWeek?.postId) {
                                                                    alert('Errore: PostId non disponibile per questa richiesta');
                                                                    setTrasmettiPendingInCorso(prev => { const n = { ...prev }; delete n[pKey]; return n; });
                                                                    return;
                                                                }
                                                                try {
                                                                    const response = await fetch(`${API_URL}/piano-ferie/interacta/conferma`, {
                                                                        method: 'POST',
                                                                        headers: { 'Content-Type': 'application/json' },
                                                                        body: JSON.stringify({ postId: ferieWeek.postId })
                                                                    });
                                                                    if (response.ok) {
                                                                        setFerieData(prev => {
                                                                            const newFerieData = { ...prev };
                                                                            const dipFerie = [...(newFerieData[dipId]?.[annoEff] || [])];
                                                                            const updatedFerie = dipFerie.map(fw => {
                                                                                if (fw.weekNum === f.weekNum && fw.stato === 'nuova_richiesta') {
                                                                                    return { ...fw, stato: 'confermata' };
                                                                                }
                                                                                return fw;
                                                                            });
                                                                            newFerieData[dipId] = { ...newFerieData[dipId], [annoEff]: updatedFerie };
                                                                            return newFerieData;
                                                                        });
                                                                    } else {
                                                                        const errorData = await response.json();
                                                                        alert(`Errore: ${errorData.error}`);
                                                                    }
                                                                } catch (error) {
                                                                    console.error('Errore nella trasmissione conferma:', error);
                                                                    alert('Errore nella trasmissione conferma');
                                                                }
                                                                setTrasmettiPendingInCorso(prev => { const n = { ...prev }; delete n[pKey]; return n; });
                                                            }}
                                                        >
                                                            {isTrasmitting ? (
                                                                <div className="btn-spinner-small" />
                                                            ) : (
                                                                <>
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                                                    </svg>
                                                                    Trasmetti
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Sezione 2: Spostamenti in Bozza */}
                                <div className="pending-section">
                                    <div className="pending-section-header">
                                        <span className="pending-section-icon" style={{ background: '#E0F2FE' }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2">
                                                <polyline points="15 10 20 15 15 20"></polyline>
                                                <path d="M4 4v7a4 4 0 0 0 4 4h12"></path>
                                            </svg>
                                        </span>
                                        <span className="pending-section-title">Spostamenti in Bozza</span>
                                        <span className="pending-section-count" style={{ background: '#E0F2FE', color: '#0369A1' }}>{spostamentiDip.length}</span>
                                    </div>
                                    {spostamentiDip.length === 0 ? (
                                        <p className="pending-empty">Nessuno spostamento in bozza.</p>
                                    ) : (
                                        <div className="pending-list">
                                            {spostamentiDip.map(s => {
                                                const pKey = `spostamento-${s.key}`;
                                                const isTrasmitting = trasmettiPendingInCorso[pKey];
                                                return (
                                                    <div key={pKey} className="pending-item">
                                                        <div className="pending-item-info">
                                                            <span className="pending-week-badge" style={{ background: '#FEE2E2', color: '#DC2626' }}>
                                                                S{s.weekFrom}
                                                            </span>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" style={{ flexShrink: 0 }}>
                                                                <path d="M5 12h14"></path>
                                                                <path d="m12 5 7 7-7 7"></path>
                                                            </svg>
                                                            <span className="pending-week-badge" style={{ background: '#E0F2FE', color: '#0369A1' }}>
                                                                S{s.weekTo}
                                                            </span>
                                                            <span className="pending-item-dates">
                                                                {s.weekToData ? formatWeekRange(s.weekToData) : ''}
                                                            </span>
                                                            <span className="stato-badge" style={{ backgroundColor: STATI_FERIE.spostata_bozza.color, fontSize: '11px', padding: '2px 8px' }}>
                                                                {STATI_FERIE.spostata_bozza.label}
                                                            </span>
                                                        </div>
                                                        <button
                                                            className="btn-pending-trasmetti"
                                                            disabled={isTrasmitting}
                                                            onClick={async () => {
                                                                if (!window.confirm(`Sei sicuro di voler trasmettere lo spostamento da S${s.weekFrom} a S${s.weekTo}?`)) return;
                                                                setTrasmettiPendingInCorso(prev => ({ ...prev, [pKey]: true }));
                                                                await _doTrasmettiSpostamento(dipId, s.weekTo);
                                                                setTrasmettiPendingInCorso(prev => { const n = { ...prev }; delete n[pKey]; return n; });
                                                            }}
                                                        >
                                                            {isTrasmitting ? (
                                                                <div className="btn-spinner-small" />
                                                            ) : (
                                                                <>
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <line x1="22" y1="2" x2="11" y2="13"></line>
                                                                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                                                    </svg>
                                                                    Trasmetti
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Sezione 3: Bozze (nuova richiesta non inviata) */}
                                <div className="pending-section">
                                    <div className="pending-section-header">
                                        <span className="pending-section-icon" style={{ background: '#F3F4F6' }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                        </span>
                                        <span className="pending-section-title">Bozze Richieste</span>
                                        <span className="pending-section-count" style={{ background: '#F3F4F6', color: '#374151' }}>{bozzeDip.length}</span>
                                    </div>
                                    {bozzeDip.length === 0 ? (
                                        <p className="pending-empty">Nessuna bozza presente.</p>
                                    ) : (
                                        <div className="pending-list">
                                            {bozzeDip.map(b => (
                                                <div key={b.key} className="pending-item">
                                                    <div className="pending-item-info">
                                                        <span className="pending-week-badge" style={{ background: '#F3F4F6', color: '#374151' }}>
                                                            S{b.weekNum}
                                                        </span>
                                                        <span className="pending-item-dates">
                                                            {b.weekData ? formatWeekRange(b.weekData) : ''}
                                                        </span>
                                                        <span className="stato-badge" style={{ backgroundColor: STATI_FERIE.bozza.color, fontSize: '11px', padding: '2px 8px' }}>
                                                            {STATI_FERIE.bozza.label}
                                                        </span>
                                                    </div>
                                                    <button
                                                        className="btn-pending-trasmetti"
                                                        disabled={trasmettiPendingInCorso[b.key]}
                                                        title="Trasmetti bozza a Interacta"
                                                        onClick={async () => {
                                                            setTrasmettiPendingInCorso(prev => ({ ...prev, [b.key]: true }));
                                                            try {
                                                                const response = await fetch(`${API_URL}/piano-ferie/bozze/trasmetti`, {
                                                                    method: 'POST',
                                                                    headers: { 'Content-Type': 'application/json' },
                                                                    body: JSON.stringify({
                                                                        dipendenteId: dipId,
                                                                        weekNum: b.weekNum,
                                                                        anno: b.anno,
                                                                        dipendenteInteractaId: dipId
                                                                    })
                                                                });
                                                                if (response.ok) {
                                                                    const result = await response.json();
                                                                    // Rimuovi bozza dallo stato locale
                                                                    setBozzeData(prev => {
                                                                        const newData = { ...prev };
                                                                        delete newData[`${dipId}-${b.weekNum}-${b.anno}`];
                                                                        return newData;
                                                                    });
                                                                    // Aggiungi ferie locali (stato spostata)
                                                                    const postId = result.postId || null;
                                                                    if (b.weekData) {
                                                                        setFerieData(prev => {
                                                                            const newData = { ...prev };
                                                                            if (!newData[dipId]) newData[dipId] = {};
                                                                            if (!newData[dipId][b.anno]) newData[dipId][b.anno] = [];
                                                                            const startDate = new Date(b.weekData.startDate);
                                                                            for (let d = 0; d < 7; d++) {
                                                                                const date = new Date(startDate);
                                                                                date.setDate(startDate.getDate() + d);
                                                                                const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                                                                                newData[dipId][b.anno] = [
                                                                                    ...newData[dipId][b.anno],
                                                                                    { date: dateStr, stato: 'spostata', weekNum: b.weekNum, postId }
                                                                                ];
                                                                            }
                                                                            return newData;
                                                                        });
                                                                        setStatsPerDipendente(prev => {
                                                                            const newStats = { ...prev };
                                                                            const base = newStats[dipId] || { giorniRichiesti: 0, giorniConfermati: 0, settimaneContate: 0, postIdContati: 0, settimaneEstive: 0, superaLimite: false, errori: {} };
                                                                            newStats[dipId] = {
                                                                                ...base,
                                                                                giorniRichiesti: base.giorniRichiesti + 7,
                                                                                settimaneContate: base.settimaneContate + 1,
                                                                                postIdContati: base.postIdContati + (postId ? 1 : 0)
                                                                            };
                                                                            return newStats;
                                                                        });
                                                                    }
                                                                } else {
                                                                    const data = await response.json();
                                                                    alert(`Errore: ${data.error || 'Errore sconosciuto'}`);
                                                                }
                                                            } catch (error) {
                                                                console.error('Errore trasmissione bozza:', error);
                                                                alert(`Errore: ${error.message}`);
                                                            } finally {
                                                                setTrasmettiPendingInCorso(prev => ({ ...prev, [b.key]: false }));
                                                            }
                                                        }}
                                                    >
                                                        {trasmettiPendingInCorso[b.key] ? (
                                                            <div className="btn-spinner-small" />
                                                        ) : (
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                                                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                                            </svg>
                                                        )}
                                                        {trasmettiPendingInCorso[b.key] ? 'Invio...' : 'Trasmetti'}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer con trasmetti tutte */}
                            {totalePending > 0 && (
                                <div className="pending-modal-footer">
                                    <button
                                        className="btn-pending-chiudi"
                                        onClick={() => setShowDipendentePendingModal(null)}
                                    >
                                        Chiudi
                                    </button>
                                    <button
                                        className="btn-pending-trasmetti-tutte"
                                        disabled={trasmettiPendingInCorso['_trasmetti_tutte']}
                                        onClick={async () => {
                                            if (!window.confirm(`Sei sicuro di voler trasmettere tutte le ${totalePending} richieste pendenti per ${dipName}?`)) return;
                                            setTrasmettiPendingInCorso(prev => ({ ...prev, '_trasmetti_tutte': true }));
                                            try {
                                            // Trasmetti tutte le nuove richieste
                                            for (const f of nuoveRichieste) {
                                                const annoEff = f._anno;
                                                const ferie = ferieData[dipId]?.[annoEff] || [];
                                                const ferieWeek = ferie.find(fw => fw.weekNum === f.weekNum && fw.stato === 'nuova_richiesta');
                                                if (ferieWeek?.postId) {
                                                    try {
                                                        const response = await fetch(`${API_URL}/piano-ferie/interacta/conferma`, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ postId: ferieWeek.postId })
                                                        });
                                                        if (response.ok) {
                                                            setFerieData(prev => {
                                                                const newFerieData = { ...prev };
                                                                const dipFerie = [...(newFerieData[dipId]?.[annoEff] || [])];
                                                                const updatedFerie = dipFerie.map(fw => {
                                                                    if (fw.weekNum === f.weekNum && fw.stato === 'nuova_richiesta') {
                                                                        return { ...fw, stato: 'confermata' };
                                                                    }
                                                                    return fw;
                                                                });
                                                                newFerieData[dipId] = { ...newFerieData[dipId], [annoEff]: updatedFerie };
                                                                return newFerieData;
                                                            });
                                                        }
                                                    } catch (error) {
                                                        console.error('Errore nella trasmissione conferma massiva:', error);
                                                    }
                                                }
                                            }
                                            // Trasmetti tutti gli spostamenti in bozza (senza confirm individuale)
                                            for (const s of spostamentiDip) {
                                                await _doTrasmettiSpostamento(dipId, s.weekTo);
                                            }
                                            // Trasmetti tutte le bozze
                                            for (const b of bozzeDip) {
                                                try {
                                                    const response = await fetch(`${API_URL}/piano-ferie/bozze/trasmetti`, {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        body: JSON.stringify({
                                                            dipendenteId: dipId,
                                                            weekNum: b.weekNum,
                                                            anno: b.anno,
                                                            dipendenteInteractaId: dipId
                                                        })
                                                    });
                                                    if (response.ok) {
                                                        const result = await response.json();
                                                        setBozzeData(prev => {
                                                            const newData = { ...prev };
                                                            delete newData[`${dipId}-${b.weekNum}-${b.anno}`];
                                                            return newData;
                                                        });
                                                        const postId = result.postId || null;
                                                        if (b.weekData) {
                                                            setFerieData(prev => {
                                                                const newData = { ...prev };
                                                                if (!newData[dipId]) newData[dipId] = {};
                                                                if (!newData[dipId][b.anno]) newData[dipId][b.anno] = [];
                                                                const startDate = new Date(b.weekData.startDate);
                                                                for (let d = 0; d < 7; d++) {
                                                                    const date = new Date(startDate);
                                                                    date.setDate(startDate.getDate() + d);
                                                                    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                                                                    newData[dipId][b.anno] = [
                                                                        ...newData[dipId][b.anno],
                                                                        { date: dateStr, stato: 'spostata', weekNum: b.weekNum, postId }
                                                                    ];
                                                                }
                                                                return newData;
                                                            });
                                                        }
                                                    }
                                                } catch (error) {
                                                    console.error('Errore trasmissione bozza massiva:', error);
                                                }
                                            }
                                            } finally {
                                                setTrasmettiPendingInCorso(prev => ({ ...prev, '_trasmetti_tutte': false }));
                                            }
                                            setShowDipendentePendingModal(null);
                                        }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="22" y1="2" x2="11" y2="13"></line>
                                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                        </svg>
                                        Trasmetti tutte ({totalePending})
                                    </button>
                                </div>
                            )}
                            {totalePending === 0 && (
                                <div className="pending-modal-footer">
                                    <button
                                        className="btn-pending-chiudi"
                                        onClick={() => setShowDipendentePendingModal(null)}
                                    >
                                        Chiudi
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* Tooltip errori (position: fixed, fuori da overflow) */}
            {alertTooltip && (
                <div
                    className="alert-tooltip-fixed"
                    style={{ left: alertTooltip.x, top: alertTooltip.y }}
                >
                    <div className="alert-tooltip-header">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                        Attenzione
                    </div>
                    <ul className="alert-tooltip-list">
                        {alertTooltip.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                        ))}
                    </ul>
                </div>
            )}

        </DndProvider>
    );
};

export default PianoFerie;
